import React, {useEffect, useState, useRef, useLayoutEffect } from 'react';
import "mathlive";
import { MathfieldElement } from "mathlive";
import { FaArrowRight, FaArrowLeft, FaCheck } from "react-icons/fa";
import { FaBookmark, FaRegBookmark, FaArrowPointer } from "react-icons/fa6";
import { PiListBulletsBold, PiNote } from "react-icons/pi";
import { SiZenn } from "react-icons/si";
import { IoMdPerson, IoMdArrowDropdown, IoMdRadioButtonOn, IoMdRadioButtonOff, IoMdMove } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { RiCheckboxFill, RiCheckboxBlankLine } from "react-icons/ri";
import { TfiTimer } from "react-icons/tfi";
import { MdArrowDropDown } from "react-icons/md";
import { MULTIPLE_CHOICES, DIGITAL_TIMER_WARNING_TIME } from 'global/constants';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import { escapeHtmlAttr } from 'global/escapeHtml';
import { plainizeMath } from 'global/plainizeMath';
import moment from 'moment';
import Button from 'components/Button';
import { PassageInterstitial } from '../PassageInterstitial';
import { PassageSetStart } from '../PassageSetStart';
import { PassageReviewList, PassageReviewEntry, ReviewTab } from '../PassageReviewList';
import { SectionDirectionsScreen } from '../SectionDirectionsScreen';
import { SectionStartPicker } from '../SectionStartPicker';
import { SectionEndScreen } from '../SectionEndScreen';
import { MagnifierLens } from '../MagnifierLens';
import { resolveSectionDirections, READING_COMP_DIRECTIONS } from '../sectionDirections';
import {
    computePassageSets,
    findPassageSet,
    computeStandaloneBlocks,
    findStandaloneBlock,
} from '../passageSets';
import {
    hasSectionChoice,
    sectionDisplayLabel,
    sectionEndTitle,
    isElaSection,
    STANDALONE_ITEMS_LABEL,
    StartSection,
} from '../sectionOrder';
import { validateMathEntry } from '../mathFillIn';
import './index.css';
import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
// import logo from '../../../../assets/images/WellEdLogoWhite.webp';
import Dropdown2 from 'components/Dropdown2';
import Dropdown3 from 'components/Dropdown3';
import LoadingPopup from 'components/LoadingPopup';
import SHSATExitPopup from 'components/SHSATExitPopup';
import AnnotationSHSATHelper from 'components/AnnotationSHSATHelper';
import DragAndDropBucket from './DragAndDropBucket';
import DragAndDropBlank from './DragAndDropBlank';
import TableGrid from './TableGrid';
import DigitalACTDirection from '../DigitalSHSATDirection';
import { current } from '@reduxjs/toolkit';
import { render } from 'react-dom';

type ModuleProps = {
    id: string;
    moduleName: string;
    number: number;
    subject: string;
    accomodation: string;
    duration: number;
    prompt: string;
    questions: {
      id: string;
      questionNum: number;
      question: string;
      questionType: number;
      // Marker (e.g. 'REVISING/EDITING PART A') -> show a directions
      // interstitial before this question; null/absent = none.
      sectionDirections?: string | null;
      questionHelper: {
        id: string;
        questionHelper: string;
      } | null;
      subject: string;
      passages: {
        id: string;
        introduction: string;
        passage: string;
        passageType: string;
        indices: {
          startIndex: number;
          endIndex: number;
          definition: string;
        }[];
        passageFigures: {
          figure: {
            id: string;
            image: string | null;
          };
          index: number;
        }[];
      } | null;
      figures: {
        id: string;
        image: string | null;
        order: number;
      }[];
      choices: {
        id: string;
        choice: string;
        figure: string | null;
        isCorrect: boolean;
      }[];
      openEndedAnswers: {
        index: number;
        answer: string;
      }[];
      hotText: {
        hotTextId: string;
        index: number;
        answer: string;
        isCorrect: boolean;
      }[];
      buckets?: {
        bucketId: string;
        index: number;
        name?: string;
        maxItems?: number;
      }[];
      dragAndDropItems?: {
        id: string;
        index: number;
        answer: string;
      }[];
      tableGridRows?: {
        id: string;
        index: number;
        name: string;
      }[];
      tableGridColumns?: {
        id: string;
        index: number;
        name: string;
      }[];
      inlineChoices?: {
        index: number;
        name: string;
      }[];
    }[];
    answers: {
      questionId: string;
      questionNum: number;
      answer: (string | null) | (string[] | string | number | null)[];
      marked: boolean;
      crossedOut: string[];
      masking: string[];   
        annotations: Array<{
            passageId: string;
            startIndex: number;
            endIndex: number;
            annotation: string;
            html?: string;
            preview?: string;
            color: string;
        }>;
      timeSpent: number;
    }[];
}
type DigitalModuleProps = {
    start: boolean;
    /** "Start with ELA or Math first" (Part A). Owned by the container
     * (which applies it in beginTest via orderByChoice); the start screen
     * renders the picker when both sections are present. Not persisted. */
    startSection?: StartSection;
    setStartSection?: (value: StartSection) => void;
    notes: string;
    studentName: string;
    testName: string;
    testNumber: number;
    timeLeft?: number;
    isUntimed?: boolean;
    questionNumber: number;
    /** When true, restrict navigation to adaptive-style: back only within
     * the current passage, no question navigator / Review screen. Driven by
     * Test.lockNavigation. Undefined/false = standard free navigation. */
    lockNavigation?: boolean;
    currentModule: ModuleProps;
    modules: ModuleProps [];
    isAlertPopupOpen: boolean;
    beginTest: () => void;
    handlePopup: (isOpen: boolean) => void;
    navigateQuestion: (index: number, moduleId?: string) => void;
    handleSelect: (event: any) => void;
    handleHotText: (text: string, index: number) => void
    handleFillintheBlank: (value: string, index: number) => void
    handleInlineChoice: (index: number, answer: string) => void
    handleHotspotFigure: (event: any) => void;
    handleEquationEditor: (value: string) => void
    markQuestion: (event: any) => void;
    crossOutChoice: (event: any) => void;
    saveAnnotation?: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
    }) => void;
    deleteAnnotation?: (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
    }) => void;
    handleSubmit: () => void;
    handleDiscardAndExit: () => void;
    handleSaveAndExit: () => void;
    saveNotes: (value: string) => void;
    autoSave: (modules: ModuleProps[]) => void;
    setModules: (modules: ModuleProps[]) => void;
    /** Opens the container-owned Contrast Settings modal (theming tool). */
    onOpenContrast?: () => void;
};

const NAVIGATORFILTER = ['All Questions', 'Not Answered', 'Bookmarks']
const TOOLS = ['Pointer', 'Answer Eliminator', 'Global Notepad'];
// Dropdown (user-menu) tools. Each entry is [defaultLabel, activeLabel];
// membership in ``dropdownToolsSelected`` toggles the tool on/off.
const DROPDOWNTOOLS = [
    ['Enable Magnifier', 'Disable Magnifier'],
    ['Show Line Reader Mask', 'Hide Line Reader Mask'],
];
const COLORS = {
    'Blue': 'blue',
    'Pink': 'pink'
};

// const INDEXFILTER = {'All questions': 0, 'Flagged': 0, 'Answered': 0, 'Unanswered': 0}
// const INITIALTOOLS = ['Line Reader', 'Magnifier'];
// const MATHTOOLS = ['Answer Eliminator', 'Answer Masking', 'Scientific Calculator', 'Line Reader', 'Magnifier'];

function DigitalSHSATModule({
    start,
    startSection,
    setStartSection,
    notes,
    studentName, 
    testName,
    testNumber,
    timeLeft,
    isUntimed,
    questionNumber,
    lockNavigation,
    currentModule,
    modules,
    isAlertPopupOpen,
    beginTest,
    handlePopup,
    navigateQuestion,
    handleSelect,
    handleHotText,
    handleFillintheBlank,
    handleInlineChoice,
    handleHotspotFigure,
    handleEquationEditor,
    markQuestion,
    crossOutChoice,
    saveAnnotation,
    deleteAnnotation,
    handleSubmit,
    handleDiscardAndExit,
    handleSaveAndExit,
    saveNotes,
    autoSave,
    setModules,
    onOpenContrast,
}: DigitalModuleProps) {
    const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(false);
    const [isReviewOpen, setIsReviewOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isShowTimer, setIsShowTimer] = useState(false);
    const [isExitOpen, setIsExitOpen] = useState(false);
    const [currentDefinition, setCurrentDefinition] = useState(null);
    const [navigatorFilter, setNavigatorFilter] = useState(NAVIGATORFILTER[0]);
    // Per-passage-set Review tab (locked flow) + EPHEMERAL per-set bookmarks
    // (no DB). ``bookmarkedQuestions`` holds question numbers bookmarked in the
    // CURRENT passage set; it's reset when the student moves to another set
    // (see the lockCurPassageId effect below).
    const [passageReviewTab, setPassageReviewTab] = useState<ReviewTab>('All Questions');
    const [bookmarkedQuestions, setBookmarkedQuestions] = useState<Set<number>>(new Set());
    const [currentQuestion, setCurrentQuestion] = useState(null);
    // lockNavigation: when the student finishes a passage set, the
    // end-of-passage interstitial (page 11 of the printed test) is shown
    // before they advance. The blue forward arrow then advances (locking
    // the passage); back / Review re-enter it. Mirrors the adaptive flow.
    const [showPassageInterstitial, setShowPassageInterstitial] = useState(false);
    // Question numbers whose section-directions screen has been dismissed
    // (blue forward arrow). A Set so it shows once per question and back-nav
    // doesn't re-show it. See resolveSectionDirections + showSectionDirections.
    const [directionsAck, setDirectionsAck] = useState<Set<number>>(new Set());
    // Passage ids whose per-set START screen ("ELA – Passage Set X of N /
    // n Questions / Start") has been dismissed. A Set so each set's start
    // screen shows once and within-passage back navigation doesn't
    // re-show it. See showPassageSetStart below.
    const [passageStartAck, setPassageStartAck] = useState<Set<string>>(new Set());
    // First question numbers of stand-alone (Part B) blocks whose START
    // screen ("ELA – Stand alone items / N Questions / Start") has been
    // dismissed. Keyed by the block's first question number. See
    // showStandaloneStart below.
    const [standaloneStartAck, setStandaloneStartAck] = useState<Set<number>>(new Set());
    // When true, the end-of-section screen ("End of {section}") is shown
    // for a NON-final section that the student just finished; Continue
    // advances to the next section. Locked-flow only. The FINAL section
    // keeps the existing terminal end screen (re-titled per-section).
    const [showSectionEnd, setShowSectionEnd] = useState(false);
    // When true, the reading-comprehension directions overlay is shown — a
    // re-showable overlay opened from the Review list's leading "ELA Rdg Comp
    // Directions" row (first reading passage set only). The blue forward /
    // back arrow dismisses it. Transient (not acked): it only ever shows on
    // demand from that row.
    const [showReadingDirections, setShowReadingDirections] = useState(false);
    const [toolsSelected, setToolsSelected] = useState(TOOLS[0]);
    const [dropdownToolsSelected, setDropdownToolsSelected] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState(modules?.flatMap(module => module.questions));
    const [currentAnnotation, setCurrentAnnotation] = useState(null);
    const [annotationColor, setAnnotationColor] = useState(null);
    const [fullPassage, setFullPassage] = useState<{
        html?: string;
        preview?: string;
    } | null>(null);
    const [annotation, setAnnotation] = useState<{
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
    } | null>(null);
    const [definitions, setDefinitions] = useState<Array<{
        passageId?: string;
        html?: string;
        preview?: string;
    }>>([]);
    const [questionHelper, setQuestionHelper] = useState(null);
    const [fbQuestionHelper, setFbQuestionHelper] = useState(null);
    // Transient warning shown when a Math-section fill-in rejects a non-numeric key.
    const [fbMathWarn, setFbMathWarn] = useState<string | null>(null);
    const fbWarnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [icQuestionHelper, setIcQuestionHelper] = useState(null);
    // Fill-in-the-blank (type 7) commits to ``modules`` only on blur (so the
    // input isn't rebuilt on every keystroke). For locked tests the forward
    // arrow must enable as soon as the student types — so we track a live
    // "has text" signal here, without touching ``modules``. Reset per question.
    const [lockLiveAnswered, setLockLiveAnswered] = useState(false);

    const closeReviewNavigator = () => {
        setIsReviewOpen(false);
        setNavigatorFilter(NAVIGATORFILTER[0]);
    };
    const closeDropdownNavigator = () => {
        setIsDropdownOpen(false);
    };
    const reviewOpenRef = useOutsideClickHandler(closeReviewNavigator);
    const dropdwonOpenRef = useOutsideClickHandler(closeDropdownNavigator);
    const questionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const navigatorContainerRef = useRef<HTMLDivElement | null>(null);
    const subjectRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const mathfieldRef = useRef<MathfieldElement | null>(null);

    const handleReviewOpen = () => {
        setIsReviewOpen(prev => !prev);
        setIsDropdownOpen(false);
        setNavigatorFilter(NAVIGATORFILTER[0]);
    }

    const handleDropdownToolsOpen = () => {
        setIsDropdownOpen(prev => !prev);
        setIsReviewOpen(false)
        setNavigatorFilter(NAVIGATORFILTER[0]);
    }

    // const preventNavigation = (event: BeforeUnloadEvent) => {
    //     if (timeLeft > 0) {
    //         event.preventDefault();
    //         event.returnValue = '';
    //     }
    // }

    const dragMove = (element: HTMLElement, body: HTMLElement, parent: HTMLElement, content?: HTMLElement, padding: number = 0, allowOverflow: boolean = false) => {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        const dragMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
            // if (element.id!=='magnifierDrag') {
            //     const element2 = document.getElementById(`${element.id}2`);
            //     const allElements = document.querySelectorAll('.draggable-element'); // 드래그 가능한 모든 요소들
            //     allElements.forEach((el) => {
            //         const draggableElement = el as HTMLElement;
            //         draggableElement.style.zIndex = '2';
            //     });
            //     element.style.zIndex = '3'
            //     element2.style.zIndex = '3'
            // }
            element.style.transform = "none";
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
            event.stopPropagation();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;

            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            // 🔹 부모 요소의 크기 가져오기
            const parentRect = parent.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // 🔹 새로운 위치 계산 (부모 요소 안에 머물도록 제한)
            let newTop = element.offsetTop - pos2;
            let newLeft = element.offsetLeft - pos1;

            newTop = Math.max(padding, Math.min(newTop, parentRect.height - elementRect.height - padding));
            newLeft = Math.max(padding, Math.min(newLeft, parentRect.width - elementRect.width - padding));

            //🔹 새로운 위치 적용
            element.style.top = `${newTop}px`;
            element.style.left = `${newLeft}px`;

            if (content) {
                // translate 조정
                const translateLeft = newLeft * (2*parent.offsetWidth - element.offsetWidth)/(parent.offsetWidth - element.offsetWidth);
                const translateTop = newTop * (2 * parent.offsetHeight - element.offsetHeight)/(parent.offsetHeight-element.offsetHeight);
                
                content.style.transform = `translate(-${translateLeft}px, -${translateTop}px) scale(2)`
            }
            
            // element'2' drag (drag in magnifier too)
            // const element2 = document.getElementById(`${element.id}2`);
            // if (element2) {
            //     element2.style.top = `${newTop}px`;
            //     element2.style.left = `${newLeft}px`;
            // }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
        

        body.onmousedown = (event) => {
            const isInsideContent = event.target instanceof Element &&
                (event.target.closest('#digitalSHSATModule__lineReaderResize') || event.target.closest('#digitalSHSATModule__lineReaderResize'))

            if (isInsideContent) {
                return;
            }
            dragMouseDown(event);
        };
    
        body.ontouchstart = (event) => {
            const isInsideContent = event.target instanceof Element &&
                (event.target.closest('#digitalSHSATModule__lineReaderResize') || event.target.closest('#digitalSHSATModule__lineReaderResize'))

            if (isInsideContent) return;

            dragMouseDown(event);
        };
    };
    
    // Resize (lineReader, lineReaderTransparent, magnifier screen size control)
    const popupResize = (element: HTMLElement, icon: HTMLElement, parent: HTMLElement, child?: HTMLElement) => {
        let startX = 0, startY = 0, startWidth = 0, startHeight = 0;
        let startElementLeft = 0, startElementTop = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
            startWidth = element.offsetWidth;
            startHeight = element.offsetHeight;
            startElementLeft = element.offsetLeft;
            startElementTop = element.offsetTop;
            
            if (event.type === 'mousedown') {
                startX = event.clientX;
                startY = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                startX = event.touches[0].clientX;
                startY = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }    
        };
    
        const dragElement = (event) => {
            event.preventDefault();
            event.stopPropagation();
    
            let moveX, moveY;
            if (event.type === 'mousemove') {
                moveX = event.clientX - startX + 20;
                moveY = event.clientY - startY + 20;
            } else if (event.type === 'touchmove') {
                moveX = event.touches[0].clientX - startX;
                moveY = event.touches[0].clientY - startY;
            }
    
            // 부모 요소 크기 가져오기
            const parentRect = parent.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // 부모 요소 내에서만 크기 조절 가능하도록 제한
            const maxWidth = parentRect.width - (elementRect.left - parentRect.left);
            const maxHeight = parentRect.height - (elementRect.top - parentRect.top);

            // child의 크기 가져오기 (없으면 기본값)
            const minChildWidth = child ? child.offsetWidth : 120;
            const minChildHeight = child ? child.offsetHeight : 70;

            // 새 크기 계산 (최소값과 최대값 적용)
            let newWidth = Math.max(child?minChildWidth+60:minChildWidth, Math.min(startWidth + moveX, maxWidth));
            let newHeight = Math.max(child?minChildHeight+70:minChildHeight, Math.min(startHeight + moveY, maxHeight));

            // 크기 적용
            element.style.width = `${newWidth-20}px`;
            element.style.height = `${newHeight-20}px`;

            const endX = startElementLeft + newWidth;
            const endY = startElementTop + newHeight;

            // element'2' resize (resize in magnifier too)
            const element2 = document.getElementById(`${element.id}2`);
            if (element2) {
                element2.style.width = `${newWidth-20}px`;
                element2.style.height = `${newHeight-20}px`;
            }

            // child가 있으면 크기와 위치도 같이 변경 (예: 투명 영역)
            const paddingX = 20;
            const paddingY = 20
            if (child) {
                const childWidth = child.offsetWidth;
                const childHeight = child.offsetHeight;
                const elementWidth = element.offsetWidth;
                const elementHeight = element.offsetHeight;

                const childLeft = parseFloat(child.style.left || "0");
                const childTop = parseFloat(child.style.top || "0");

                const rightEdge = childLeft + childWidth;
                const bottomEdge = childTop + childHeight;

                const insideX = rightEdge >= elementWidth - paddingX;
                const insideY = bottomEdge >= elementHeight - paddingY;

            if (insideX || insideY) {
                // x축 위치 조정
                if (insideX) {
                    child.style.left = `${elementWidth - childWidth - paddingX}px`;
                }
                // y축 위치 조정
                if (insideY) {
                    child.style.top = `${elementHeight - childHeight - paddingY}px`;
                }

                // ✅ element가 작아져서 child보다 작으면 → child 크기도 줄이기
                const availableWidth = elementWidth - paddingX * 2;
                const availableHeight = elementHeight - paddingY * 2;

                const newChildWidth = Math.max(minChildWidth, Math.min(childWidth, availableWidth));
                const newChildHeight = Math.max(minChildHeight, Math.min(childHeight, availableHeight));

                child.style.width = `${newChildWidth}px`;
                child.style.height = `${newChildHeight}px`;

                // 크기 변경 후 위치 다시 조정 (사이즈 바뀌었으니)
                if (insideX) {
                    child.style.left = `${elementWidth - newChildWidth - paddingX}px`;
                }
                if (insideY) {
                    child.style.top = `${elementHeight - newChildHeight - paddingY}px`;
                }
            }
            }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        icon.onmousedown = dragMouseDown;
        icon.ontouchstart = dragMouseDown;
    };

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');
        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        return minutes!=='00'? `${hours} hours ${minutes} mins` : `${hours} hours`;
    }

    // fullPassage
    const getPassageWithFigure = ({id: passageId, passageFigures = []}) => {
        const passage = document.getElementById(passageId);
        let passageHTML = '';
        let lastIndex = 0;
        const usedFigureIndices = new Set();

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];
            // add open tags first while see the text
            const addMarkTags = (node) => {
                if (node.outerHTML) {
                    const { innerHTML, outerHTML } = node;
                    const childOffset = outerHTML.indexOf('>') + 1;

                    passageHTML += outerHTML.slice(0, childOffset);

                    for (let i = 0; i < node.childNodes.length; i++) {
                        const child = node.childNodes[i];
                        addMarkTags(child);
                    }

                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                } 
                else {
                    // 이미지 삽입을 위한 passageFigure 검사
                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                    if (currentFigure) {
                        usedFigureIndices.add(currentFigure.figure.index);
                        passageHTML += `<div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>`;                    
                    }
                    const { textContent } = node;
                    passageHTML += textContent;
                    lastIndex += textContent.length;
                }  
            }

            addMarkTags(childNode);
        }

        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
        if (currentFigure) {
            passageHTML += `<div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>`;                    
        }
        setFullPassage({
            html: passageHTML
        });
    };

    // annotation
    const handlePassageMouseUp = () => {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }
        if (selection.toString().length === 0) {
            setCurrentAnnotation(null);
        }

        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        const passages = document.getElementsByClassName('digitalSHSATModule__passageTextPassage');
        const passage = Array.from(passages).find(passage => passage.contains(anchorNode) && passage.contains(focusNode));
        if (!passage) {
            return;
        }

        let startOffset = 0;
        let endOffset = 0;

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === anchorNode) {
                startOffset += anchorOffset;
                break;
            } 

            else if (childNode.contains(anchorNode)) {
                const childText = childNode.textContent;
                const anchorText = anchorNode.textContent;
                const indexInChild = childText.indexOf(anchorText);
                if(indexInChild === -1) {
                    console.error("anchorText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + anchorOffset;
                startOffset += lengthBeforeAnchor;
                break;
            }
            else {
                startOffset += childLength;
            }
        }

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === focusNode) {
                endOffset += focusOffset;
                break;
            } 
            else if (childNode.contains(focusNode)) {
                const childText = childNode.textContent;
                const focusText = focusNode.textContent;
                const indexInChild = childText.indexOf(focusText);
                if(indexInChild === -1) {
                    console.error("focusText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + focusOffset;
                endOffset += lengthBeforeAnchor;
                break;
            } 
            else {
                endOffset += childLength;
            }
        }

        if (startOffset > endOffset) {
            const temp = startOffset;
            startOffset = endOffset;
            endOffset = temp;
        } else if (startOffset===endOffset) {
            setCurrentAnnotation(null);
            return;
        }

        if (startOffset > endOffset) {
            [startOffset, endOffset] = [endOffset, startOffset];
        }

        // 선택이 있을 경우만 annotation 저장
        if (selection.toString().length > 0) {
            setCurrentAnnotation({
                passageId: passage.id.replace('passage ', ''),
                startIndex: startOffset,
                endIndex: endOffset,
                annotation: '',
                html: '',
                preview: '',
                color: annotationColor,
            });
        }

        let passageHTML = '';
        let passagePreview = '';
        setCurrentAnnotation({
            passageId: passage.id.replace('passage ', ''),
            startIndex: startOffset,
            endIndex: endOffset,
            html: passageHTML,
            preview: passagePreview,
            color: annotationColor,
        });
    }

    // definition
    const handleDefinition = () => {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }
        if (selection.toString().length === 0) {
            setCurrentAnnotation(null);
        }

        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        const passages = document.getElementsByClassName('digitalSHSATModule__Definition');
        const passage = Array.from(passages).find(passage => passage.contains(anchorNode) && passage.contains(focusNode));
        if (!passage) {
            return;
        }

        let startOffset = 0;
        let endOffset = 0;

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === anchorNode) {
                startOffset += anchorOffset;
                break;
            } 

            else if (childNode.contains(anchorNode)) {
                const childText = childNode.textContent;
                const anchorText = anchorNode.textContent;
                const indexInChild = childText.indexOf(anchorText);
                if(indexInChild === -1) {
                    console.error("anchorText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + anchorOffset;
                startOffset += lengthBeforeAnchor;
                break;
            }
            else {
                startOffset += childLength;
            }
        }

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === focusNode) {
                endOffset += focusOffset;
                break;
            } 
            else if (childNode.contains(focusNode)) {
                const childText = childNode.textContent;
                const focusText = focusNode.textContent;
                const indexInChild = childText.indexOf(focusText);
                if(indexInChild === -1) {
                    console.error("focusText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + focusOffset;
                endOffset += lengthBeforeAnchor;
                break;
            } 
            else {
                endOffset += childLength;
            }
        }

        if (startOffset > endOffset) {
            const temp = startOffset;
            startOffset = endOffset;
            endOffset = temp;
        }

        if (startOffset > endOffset) {
            [startOffset, endOffset] = [endOffset, startOffset];
        }

        // call definition
        const definitions = currentQuestion?.passages?.indices
        definitions.forEach((def) => {
            const {startIndex, endIndex, definition} = def
            if (startOffset >= startIndex && endOffset <= endIndex) {
                setCurrentDefinition({
                    ...def,
                    _updatedAt: Date.now() // 강제로 매번 달라지게 하는 값
                });
            }
        })
    }

    const generatePassageHTML = (passage, startOffset, endOffset, color) => {
        let passageHTML = '';
        let passagePreview = '';
        let lastIndex = 0;

        const addMarkTags = (node) => {
            if (node.nodeName === 'IMG') {
                passageHTML += node.outerHTML;
            }
            else if (node.outerHTML) {
                const { innerHTML, outerHTML } = node;
                const childOffset = outerHTML.indexOf('>') + 1;
                passageHTML += outerHTML.slice(0, childOffset);
                node.childNodes.forEach(addMarkTags);
                passageHTML += outerHTML.slice(childOffset + innerHTML.length);
            }
            else {
                const { textContent } = node;
                if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += `<mark>`;
                    passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex);
                    passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                }
                else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                    passageHTML += textContent.slice(0, startOffset - lastIndex);
                    passageHTML += `<mark>`;
                    passageHTML += textContent.slice(startOffset - lastIndex);
                    passageHTML += '</mark>';
                    passagePreview += textContent.slice(startOffset - lastIndex);
                }
                else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                    passageHTML += `<mark>`;
                    passageHTML += textContent.slice(0, endOffset - lastIndex);
                    passageHTML += '</mark>';
                    passageHTML += textContent.slice(endOffset - lastIndex);
                    passagePreview += textContent.slice(0, endOffset - lastIndex);
                }
                else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                    passageHTML += `<mark>`;
                    passageHTML += textContent;
                    passageHTML += '</mark>';
                    passagePreview += textContent;
                }
                else {
                    passageHTML += textContent;
                }
                lastIndex += textContent.length;
            }
        };

        passage.childNodes.forEach(addMarkTags);
        return { passageHTML, passagePreview };
    };

    const deleteAnnotations = () => {
        if (!currentAnnotation) return;      
        deleteAnnotation(currentAnnotation);
        setCurrentAnnotation(null);
    }

    useEffect(() => {
        if (!currentAnnotation) return;
        
        const passage = document.getElementById(currentAnnotation.passageId);
        if (!passage) return;
        const { passageHTML, passagePreview } = generatePassageHTML(
            passage,
            currentAnnotation.startIndex,
            currentAnnotation.endIndex,
            annotationColor
        );

        const updatedAnnotation = {
            ...currentAnnotation,
            html: passageHTML,
            preview: passagePreview,
            color: annotationColor
        };
        saveAnnotation(updatedAnnotation);
        setCurrentAnnotation(null);
        setAnnotationColor(null);
       
    }, [annotationColor]);

    useEffect(() => {
        if (!currentAnnotation) return;

        const annotationHelper = document.getElementById('digitalSHSATModule__passageAnnotationHelper');
        const passageContainer = document.querySelector('.digitalSHSATModule__passage');
        if (!annotationHelper || !passageContainer) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const containerRect = passageContainer.getBoundingClientRect();

        // helper 위치 계산
        let left = (rect.left - containerRect.left) + (rect.width / 2) - (annotationHelper.offsetWidth / 2);
        let top = (rect.top - containerRect.top) - annotationHelper.offsetHeight - 15;

        // 경계 내 제한
        left = Math.max(Math.min(left, containerRect.width - annotationHelper.offsetWidth), 0);
        top = Math.max(top, 0);

        annotationHelper.style.left = `${left}px`;
        annotationHelper.style.top = `${top}px`;

    }, [currentAnnotation]);

    useEffect(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        const popupWidth = 700; 
        const popupHeight = 100;
        const gap = 8;

        // selection 영역 위쪽에 뜨도록
        const top = rect.top - popupHeight - gap;
        const left = (rect.left + rect.right) / 2 - popupWidth / 2 + 160;

        setPopupPos({ top, left });
    }, [currentDefinition]);

    // Definition
    const generateDefinitionLink = ({id: passageId, indices, passageFigures = []}) => {
        for (let j = 0; j < indices.length; j++){
            const passage = document.getElementById(passageId);
            let passageHTML = '';
            let passagePreview = '';
            const usedFigureIndices = new Set();
            const { startIndex, endIndex } = indices[j];
            let startOffset = startIndex
            let endOffset = endIndex
            let lastIndex = 0;

            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];

                const addMarkTags = (node) => {
                    if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        // 이미지 삽입을 위한 passageFigure 검사
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            usedFigureIndices.add(currentFigure.figure.index);
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
                        const { textContent } = node;
    
                        if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark class="digitalSHSATModule__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                        }
                        else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark class="digitalSHSATModule__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                        }
                        else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                            passageHTML += '<mark class="digitalSHSATModule__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(0, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endOffset - lastIndex);
                        }
                        else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                            passageHTML += '<mark class="digitalSHSATModule__definitionMark" data-tooltip="?">';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }
            const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
            if (currentFigure) {
                passageHTML += `
                    <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                `;                    
            }
            
            setDefinitions(prev => [...(prev || []), { 
                passageId: passageId,
                html: passageHTML, 
                preview: passagePreview 
            }]);
        }

    };

    // Question Helper
    const generateQuestionHelper = ({ questionHelper, hotText = [], answers = [] }) => {
        if (!questionHelper) return '';

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const isSelected = answers.includes(index);
            const selectedClass = isSelected ? " selected" : "";
            return `<div class="digitalSHSATModule__questionHelperHotText${selectedClass}" data-index="${index}" data-text="${match}">${match}</div>`;
        });
        setQuestionHelper(html);
    };

    // Fill in the Blank Question Helper
    const generateFBQuestionHelper = ({ questionHelper, answers = [] }) => {
        if (!questionHelper) return '';
        // index별로 답을 묶어줌
        const groupedAnswers = {};
        answers.forEach(([idx, val]) => {
            if (!groupedAnswers[idx]) groupedAnswers[idx] = [];
            groupedAnswers[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const vals = groupedAnswers[index] || []; // 해당 index의 답 리스트
            const value = vals.length > 0 ? vals.join(", ") : "";
            const corrects = currentQuestion?.openEndedAnswers?.filter(answer => answer.index === index)
            const width = Math.max(50, corrects.reduce((acc, ans) => Math.max(acc, ans.answer?.length || 0), 0) * 10)
            return `<input class="digitalSHSATModule__questionHelperFB" style="width:${width}px" data-index="${index}" value="${value}" />`;
        });
        setFbQuestionHelper(html);
    };

    // Inline Choice Question Helper
    const generateICQuestionHelper = ({ questionHelper, inlineChoices = [], answers = [] }) => {
        if (!questionHelper) return '';
        // index별로 답을 묶어줌
        const groupedAnswers = {};
        answers.forEach(([idx, val]) => {
            if (!groupedAnswers[idx]) groupedAnswers[idx] = [];
            groupedAnswers[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;

            // index별 inlineChoices 찾아오기
            const choices = inlineChoices.filter(choice => choice.index === index);
            // Native <option> can't render KaTeX, so the label is plainized
            // (global/plainizeMath.ts). The option ``value`` keeps the raw
            // string, so grading is unaffected; both are HTML-escaped because
            // this markup is injected as a string.
            // The select sizes itself to its widest option (the browser measures
            // the real glyphs, so labels like ``10√2`` and ``$126.00`` aren't
            // clipped as they were with a ~8px/char estimate). Floor at 70px; cap
            // at 320px so a long phrase option (e.g. a 36-char answer) doesn't
            // overflow the line and wrap the dropdown onto its own row. Longer
            // options truncate in the closed select; the popup still shows them
            // full. The cap keeps the CSS column limit via min().
            const optionsHtml = choices
                .map(
                    (choice) => {
                        const isSelected = groupedAnswers[index]?.includes(choice.answer);
                        return `<option class="digitalSHSATModule__questionHelperIC" value="${escapeHtmlAttr(choice.answer || '')}" ${isSelected ? "selected":""}>${escapeHtmlAttr(plainizeMath(choice.answer || ''))}</option>`
                })
                .join("");

            const chooseSelected = groupedAnswers[index] ? "" : "selected";
            return `<select class="digitalSHSATModule__questionHelperIC" style="min-width:70px; max-width:min(320px, calc(100% - 12px))" data-index="${index}">
                        <option class="digitalSHSATModule__questionHelperIC" value="" ${chooseSelected}>Choose...</option>
                        ${optionsHtml}
                    </select>`;
        });
        setIcQuestionHelper(html);
    };

    // katex
    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        applyKatexWrap(document, {
            enabled: currentModule?.subject === 'English Language Arts',
        });

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    // Tools
    const handleTools = (tool:string) => {
        if (toolsSelected === tool) {
            setToolsSelected(TOOLS[0]);
        } else {
            setToolsSelected(tool);
        }
    };

    // DropdownTools
    const handleDropdownTools = (tool:string) => {
        if (dropdownToolsSelected.includes(tool)) {
            setDropdownToolsSelected(prevTools => prevTools.filter((value)=> value!==tool));
        } else {
            setDropdownToolsSelected(prevTools => [...prevTools, tool]);
        }
        setIsDropdownOpen(false);
    };    
    
    const loadingPopup = (
        <LoadingPopup />
    );

    const exitPopup = (
        <SHSATExitPopup 
            handleCancel = {() => setIsExitOpen(false)}
            handleSaveExit = {handleSaveAndExit}
        />
    );

    useEffect(()=>{
        // filter questions
        switch(navigatorFilter) {
            case 'All Questions':
                setFilteredQuestions(modules.flatMap(module => module.questions))
                break;
            case 'Not Answered':
                setFilteredQuestions(modules.flatMap(module => module.questions).filter(q => modules.flatMap(module => module.answers).find(a => a.questionNum === q.questionNum && (a.answer === '' || a.answer === null || a.answer.length === 0 || ((Array.isArray(a.answer) && Array.isArray(a.answer[0]))&& (a.answer as [number, string][]).filter(( [, val]) => val !== "").length === 0)))));
                break;            
            case 'Bookmarks':
                setFilteredQuestions(modules.flatMap(module => module.questions).filter(q => modules.flatMap(module => module.answers).find(a => a.questionNum === q.questionNum && a.marked)));
                break;
        }   
    }, [navigatorFilter, modules]);

    // reviewNavigator: scroll
    useEffect(() => {
        if (questionNumber === 0) {
            if (currentModule?.subject === 'English Language Arts') {
                const el = subjectRefs.current['English Language Arts'];
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }  else if (questionNumber === 0 && currentModule?.subject === 'Mathematics') {
                const el = subjectRefs.current['Mathematics'];
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } 
        } else if (questionNumber !== 0 && !modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber))){
            const el = subjectRefs.current['End Test'];
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else{
            const currentQuestionRef = questionRefs.current[questionNumber];
            const container = navigatorContainerRef.current;

            if (container && currentQuestionRef) {
                const scrollTopValue = currentQuestionRef.offsetTop - container.offsetTop - container.clientHeight / 2.5;
                container.scrollTo({
                    top: scrollTopValue,
                    behavior: "auto"
                });
            }        
        }   
    }, [questionNumber, isReviewOpen]);

    useEffect(()=>{
        setCurrentQuestion(currentModule?.questions?.find(question=> question.questionNum === questionNumber))
        setIsReviewOpen(false);
        setLoading(true); // 로딩 시작
        
        const timer = setTimeout(() => {
        setLoading(false);
        }, 200);

        return () => clearTimeout(timer);
    }, [questionNumber]);

    useEffect(() => {
        const keyboard = (window as any).mathVirtualKeyboard;

        // always visible
        if (currentQuestion?.questionType === 11 || currentQuestion?.questionType === 12) {
            mathfieldRef.current.mathVirtualKeyboardPolicy = 'manual'
        } else {
            if (mathfieldRef.current) mathfieldRef.current.mathVirtualKeyboardPolicy = 'auto';
            keyboard.hide();
        }
        
        const mf = mathfieldRef.current;
        if (mf) mf.setAttribute("virtual-keyboard-toolbar", "none");
        if (!keyboard || !mf) return;

        // keyboard blocking
        const handleKeydown = (e: KeyboardEvent) => {
            const navKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'];
            if (navKeys.includes(e.key)) return;

            // 숫자만 허용
            const allowedKeys = /^[0-9]$/.test(e.key) || ['<', '>', '/', '.', '+', '-', '=', '%', '*', '|'].includes(e.key);

            if (!allowedKeys) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        mf.addEventListener('keydown', handleKeydown);

        const handlePaste = (e: ClipboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };
        mf.addEventListener('paste', handlePaste);

        // keyboard customization
        if (currentQuestion?.questionType === 11) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","x","y",""],
                    ["6","7","8","9","0","+","−","\\sqrt{#0}"],
                    ["\\%","−",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","\\cdot","\\div","\\sqrt[#0]{#0}"],
                    ["","{#0}^{#0}","({#0})","\\le","\\lt","=","\\ge","\\gt"],
                    ["\\pi","","","","","","","\\lvert{#@}\\rvert"]
                ]
            };
            keyboard.show();
            keyboard.visible = true;
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".digitalSHSATModule__eeContainer .MLK__keycap");
                let minusCount = 0; 

                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "x": tooltip = "letter x"; break;
                        case "y": tooltip = "letter y"; break;
                        case "+": tooltip = "add"; break;
                        case "−":
                        case "-":
                            minusCount++;
                            tooltip = minusCount === 1 ? "subtract" : "negative sign";
                            break;
                        case "÷": tooltip = "divide"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "=": tooltip = "equal"; break;
                        case "≤": tooltip = "less than or equal"; break;
                        case "<": tooltip = "less than"; break;
                        case "≥": tooltip = "greater than or equal"; break;
                        case ">": tooltip = "greater than"; break;
                        case "π": tooltip = "constant pi"; break;
                        case "⋅": tooltip = "multiply dot"; break;
                        case "√▢​": tooltip = "square root"; break;
                        case "▢√▢​": tooltip = "general root"; break;
                        case "▢▢": tooltip = "exponent"; break;
                        case "(▢)": tooltip = "parentheses"; break;
                        case "∣■∣": tooltip = "absolute value"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);

        } else if (currentQuestion?.questionType === 12) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","","",""],
                    ["6","7","8","9","0","","",""],
                    ["\\%","-",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","","",""]
                ]
            }; 
            keyboard.show();
            keyboard.visible = true;   
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".digitalSHSATModule__eeContainer .MLK__keycap");
                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "−": tooltip = "negative sign"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);        
        }

        // remove toggle menu buttons, keyboard location, keyboard styling
        const style = document.createElement("style");
        style.innerHTML = `
            math-field::part(menu-toggle),
            math-field::part(virtual-keyboard-toggle) { display: none !important; }

            ${currentQuestion?.questionType === 11 || currentQuestion?.questionType === 12 ? '' : 
                'digitalSHSATModule__eeContainer .ML__virtual-keyboard { display: none !important; }'
            }

            .digitalSHSATModule__eeContainer { position: relative; }
            .digitalSHSATModule__eeContainer .ML__virtual-keyboard {
                position: absolute !important;
                top: calc(100% + 8px) !important;
                left: 0 !important;
                width: 620px !important;
                z-index: 999 !important;
            }
            .digitalSHSATModule__eeContainer .MLK__backdrop {
                background: none;
            }
            
            .digitalSHSATModule__eeContainer .MLK__keycap {
                border: 1px solid #676767;
                flex: 1;
                cursor: pointer;
            }
            .digitalSHSATModule__eeContainer .MLK__keycap:hover {
                outline: 2px solid #676767 !important;
                box-shadow: 0 0 4px rgba(233, 233, 233, 0.3);
            }
            .digitalSHSATModule__eeContainer .MLK__keycap,
            .digitalSHSATModule__eeContainer .MLK__keycap::after {
                transition: none !important;
            }
            .digitalSHSATModule__eeContainer .MLK__keycap:hover::after {
                content: attr(data-tooltip);
                position: absolute;
                top: -28px;
                transform: translateX(-30%);
                background: rgba(0,0,0,0.8);
                color: white;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 12px;
                white-space: nowrap;
                pointer-events: none;
                z-index: 1000;
            }
            .digitalSHSATModule__eeContainer .MLK__keycap:active {
                background: none !important;
            }
            .ML__latex .ML__vlist-t2,
            .ML__delim-size1 {
                font-size: 16px;
            }
            .digitalSHSATModule__eeContainer .MLK__toolbar { 
                justify-content: flex-start; 
                display: none;
            }
            .digitalSHSATModule__eeContainer .MLK__plate {
                border: 1px solid #bdbdbd;
                padding: 4px;
            }
            .digitalSHSATModule__eeContainer :where(.MLK__rows > .MLK__row div) {
                border: 1px solid #bdbdbd;
                border-radius: 1px;
                font-size: 18px;
                background: white;
            }
            .digitalSHSATModule__eeContainer .MLK__rows > .MLK__row div:empty {
                pointer-events: none;
                opacity: 0.5;         
                background-color: white;
                border: 1px solid #e0e0e0ff;
            }
            .digitalSHSATModule__eeContainer .MLK__rows {
                gap: 5px;
            }    
            .digitalSHSATModule__eeContainer .MLK__rows > .MLK__row {
                gap: 5px;
                justify-content: flex-start;
            }    
            .digitalSHSATModule__eeContainer .MLK__row:nth-child(-n+2) div:nth-child(-n+5) {
                background-color: #eeeeeec7 !important;
            }
            /* High-contrast keycap/toolbar labels. MathLive's keyboard defaults
               to a dark theme (light glyphs, --_keycap-text falls back to
               #e3e4e8); these white keys left the digits nearly invisible. Set
               the public variables and force the glyph color dark. The visible
               glyph is a .ML__cmr span inside .ML__latex inside .MLK__keycap. */
            .digitalSHSATModule__eeContainer {
                --keycap-text: #1a1a1a;
                --keycap-text-active: #1a1a1a;
                --keycap-text-hover: #1a1a1a;
                --keycap-text-pressed: #1a1a1a;
                --keycap-secondary-text: #1a1a1a;
                --keyboard-toolbar-text: #1a1a1a;
                --keyboard-toolbar-text-active: #1a1a1a;
            }
            .digitalSHSATModule__eeContainer .MLK__keycap:not(:empty),
            .digitalSHSATModule__eeContainer .MLK__keycap:not(:empty) *,
            .digitalSHSATModule__eeContainer .ML__latex,
            .digitalSHSATModule__eeContainer .ML__latex * {
                color: #1a1a1a !important;
            }
            .digitalSHSATModule__eeContainer .MLK__toolbar .action svg {
                fill: currentColor !important;
            }
        `;
        document.head.appendChild(style);

        // keyboard location
        const container = document.querySelector(".digitalSHSATModule__eeContainer");
        let observer: MutationObserver | null = null;
        if (container) {
            keyboard.container = container;
            keyboard.alwaysVisible = true;
            keyboard.floater = false;
            keyboard.autoDismiss = false;
            keyboard.show();
        }
        setTimeout(() => {
            const vk = container?.querySelector(".ML__keyboard") as HTMLElement;
            if (vk) {
                // keyboard location styling
                vk.style.height = "260px";
                vk.style.pointerEvents = "auto";
                vk.style.opacity = "1";
                vk.style.visibility = "visible";
                vk.style.removeProperty('--_keyboard-height'); 
                vk.style.height = '300px';    

                // toolbar
                const setupToolbar = () => {
                    const toolbar = vk.querySelector(".MLK__toolbar") as HTMLElement;
                    if (!toolbar) return;
                    toolbar.style.display = "flex";
                    // remove copy / paste button
                    const blockedCommands = ["pasteFromClipboard", "copyToClipboard"];
                    const actionButtons = toolbar.querySelectorAll('[data-command]');
                    actionButtons.forEach(btn => {
                        const cmd = btn.getAttribute("data-command") || "";
                        if (blockedCommands.some(c => cmd.includes(c))) {
                            btn.remove();
                        }
                    });

                    // Add delete all button
                    if (!toolbar.querySelector('[data-command="deleteAll"]')) {
                        const deleteAllBtn = document.createElement("div");
                        deleteAllBtn.className = "action";
                        deleteAllBtn.setAttribute("data-command", "deleteAll");
                        deleteAllBtn.setAttribute("data-tooltip", "모두 지우기");

                        deleteAllBtn.innerHTML = `
                            <svg viewBox="0 0 20 20" width="18" height="20">
                                <path fill="currentColor" d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4Z"/>
                            </svg>
                        `;

                        deleteAllBtn.addEventListener("click", () => {
                            const mf = mathfieldRef.current;
                            if (mf) mf.setValue("");
                            handleEquationEditor("")
                        });

                        Object.assign(deleteAllBtn.style, {
                            display: "flex",           
                            alignItems: "center",       
                            justifyContent: "center",   
                            padding: "4px",            
                            cursor: "pointer",          
                            color: "#333",              
                            marginRight: "8px",        
                            height: "32px",            
                            minWidth: "32px",           
                            boxSizing: "border-box",    
                        });
                        toolbar.appendChild(deleteAllBtn);
                    }
                };

                // 처음 한 번 실행
                setupToolbar();
                // MathLive가 toolbar 다시 그릴 때마다 다시 적용
                const toolbarObserver = new MutationObserver(() => {
                    setupToolbar();
                });
                toolbarObserver.observe(vk, { childList: true, subtree: true });

                // always keyboard visible
                observer = new MutationObserver(() => {
                    if (
                        vk.style.display === "none" ||
                        vk.style.visibility === "hidden" ||
                        vk.style.opacity === "0"
                    ) {
                        vk.style.display = "block";
                        vk.style.visibility = "visible";
                        vk.style.opacity = "1";
                        vk.style.pointerEvents = "auto";
                    }
                });
                observer.observe(vk, {
                    attributes: true,
                    attributeFilter: ["style"],
                    subtree: false,
                });
            }
            if (mf) {
                // always initially
                mf.focus();
                // input focus style
                mf.addEventListener("focusin", () => { mf.style.border = "2px solid #2f80ed";});
                mf.addEventListener("focusout", () => { mf.style.border = "1px solid #bdbdbd";});
            }
        }, 50);

        return () => {
            // style 제거
            document.head.removeChild(style);
            // observer 해제
            if (observer) observer.disconnect();
            mf.removeEventListener('keydown', handleKeydown);
            mf.removeEventListener('paste', handlePaste);
        };
    }, [currentQuestion]);

    useEffect(() => {
        if (isAlertPopupOpen) {
            const timer = setTimeout(() => {
                handlePopup(false); 
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [isAlertPopupOpen]);

    useLayoutEffect(() => {
        renderKatex();
        setFullPassage(null);
        setDefinitions([]);
        setQuestionHelper(null);
        const passageData = currentQuestion?.passages
        if (passageData && (questionNumber <= modules?.map(m => m.questions).flat().length && questionNumber > 0)) {
            const { id, indices, passageFigures } = currentQuestion?.passages
            getPassageWithFigure({ id, passageFigures });
            if (indices) generateDefinitionLink({ id, indices, passageFigures });   
        }
        // question helper
        if (currentQuestion?.questionHelper) {
            if (currentQuestion?.questionType === 4) {
                generateQuestionHelper({ 
                    questionHelper: currentQuestion?.questionHelper, 
                    hotText: currentQuestion?.hotText || [],
                    answers: currentModule?.answers?.find(answer => answer.questionNum === currentQuestion.questionNum)?.answer as any[] || []
                });
            } else if (currentQuestion?.questionType === 7) {
                generateFBQuestionHelper({ 
                    questionHelper: currentQuestion?.questionHelper, 
                    answers: currentModule?.answers?.find(answer => answer.questionNum === currentQuestion.questionNum)?.answer as any[] || []
                });
            } else if (currentQuestion?.questionType === 9) {
                generateICQuestionHelper({ 
                    questionHelper: currentQuestion?.questionHelper, 
                    inlineChoices: currentQuestion?.inlineChoices || [],
                    answers: currentModule?.answers?.find(answer => answer.questionNum === currentQuestion.questionNum)?.answer as any[] || []
                });
            }
        } 
    }, [currentQuestion, questionNumber, start]);

    useEffect(()=>{
        renderKatex();
        setIsReviewOpen(false);
        // question helper
        if (currentQuestion?.questionHelper) {
            if (currentQuestion?.questionType === 4) {
                generateQuestionHelper({ 
                    questionHelper: currentQuestion?.questionHelper, 
                    hotText: currentQuestion?.hotText || [],
                    answers: currentModule?.answers?.find(answer => answer.questionNum === currentQuestion.questionNum)?.answer as any[] || []
                });
            } else if (currentQuestion?.questionType === 7) {
                generateFBQuestionHelper({ 
                    questionHelper: currentQuestion?.questionHelper, 
                    answers: currentModule?.answers?.find(answer => answer.questionNum === currentQuestion.questionNum)?.answer as any[] || []
                });
            } else if (currentQuestion?.questionType === 9) {
                generateICQuestionHelper({ 
                    questionHelper: currentQuestion?.questionHelper, 
                    inlineChoices: currentQuestion?.inlineChoices || [],
                    answers: currentModule?.answers?.find(answer => answer.questionNum === currentQuestion.questionNum)?.answer as any[] || []
                });
            }
        } 
    }, [currentModule]);

    useEffect(()=>{
        renderKatex();
    }, [questionHelper, fbQuestionHelper, icQuestionHelper]);

    // Clear the live fill-in signal whenever the question changes; the lock
    // gate then relies on the committed answer (or a fresh keystroke).
    useEffect(() => {
        setLockLiveAnswered(false);
    }, [questionNumber]);

    useEffect(() => {
        const LineReaderDragEl = document.getElementById('lineReaderDrag');
        const LineReaderBodyEl = document.getElementById('lineReaderDrag');
        const LineReaderParentEl = document.getElementById('digitalSHSATModule__body');
        if (LineReaderDragEl && LineReaderBodyEl && LineReaderParentEl) {
            dragMove(LineReaderDragEl, LineReaderBodyEl, LineReaderParentEl, undefined, 0, true);
        }
        
        // line Reader transparent move
        const lineReaderControlDragEl = document.getElementById('digitalSHSATModule__popuplineReaderContent');
        const lineReaderControlBarEl = document.getElementById('digitalSHSATModule__lineReaderControlBar');
        const lineReaderParentEl = document.getElementById('digitalSHSATModule__popuplineReader');
        if (lineReaderControlDragEl && lineReaderControlBarEl && lineReaderParentEl) {
            dragMove(lineReaderControlDragEl, lineReaderControlBarEl, lineReaderParentEl, undefined, 20);
        }

        // line Reader popup resizing
        const lineReaderResizeEl = document.getElementById('lineReaderDrag');
        const lineReaderResizeIconEl = document.getElementById('digitalSHSATModule__lineReaderResize');
        const lineReaderResizeParentEl = document.getElementById('digitalSHSATModule__body');
        const lineReaderResizeChildEl = document.getElementById('digitalSHSATModule__popuplineReaderContent');
        if (lineReaderResizeEl && lineReaderResizeIconEl && lineReaderResizeParentEl && lineReaderResizeChildEl) {
            popupResize(lineReaderResizeEl, lineReaderResizeIconEl, lineReaderResizeParentEl, lineReaderResizeChildEl);
        }

        // line Reader transparent resizing
        const transparentResizeEl = document.getElementById('digitalSHSATModule__popuplineReaderContent');
        const transparentResizeIconEl = document.getElementById('digitalSHSATModule__lineReaderTransparentResize');
        const transparentResizeParentEl = document.getElementById('lineReaderDrag');
        if (transparentResizeEl && transparentResizeIconEl && transparentResizeParentEl) {
            popupResize(transparentResizeEl, transparentResizeIconEl, transparentResizeParentEl);
        }

        // notepad move
        const notepadDragEl = document.getElementById('notepadDrag');
        const notepadHeaderEl = document.getElementById('notepadHeader');
        const notepadParentEl = document.getElementById('digitalSHSATModule');
        if (notepadDragEl && notepadHeaderEl && notepadParentEl) {
            dragMove(notepadDragEl, notepadHeaderEl, notepadParentEl);
        }

        // definition move
        const definitionDragEl = document.getElementById('definitionDrag');
        const definitionHeaderEl = document.getElementById('definitionHeader');
        const definitionParentEl = document.getElementById('digitalSHSATModule');
        if (definitionDragEl && definitionHeaderEl && definitionParentEl) {
            dragMove(definitionDragEl, definitionHeaderEl, definitionParentEl);
        }
    }, []);

    // ---- lockNavigation gating (adaptive-style nav for fixed-form) ----
    // Computed off the ordered question list + each question's passage id
    // (``passages.id`` = the unique passageId), so it's robust to the
    // legacy ``questionNumber===0`` intro state and module boundaries.
    const lockOrderedQuestions = currentModule?.questions ?? [];
    const lockCurIdx = lockOrderedQuestions.findIndex(
        (q) => q.questionNum === questionNumber
    );
    const lockCurPassageId = lockCurIdx >= 0
        ? (lockOrderedQuestions[lockCurIdx]?.passages?.id ?? null)
        : null;
    // Ephemeral per-passage-set bookmarks reset when the student moves to
    // another passage set (locked flow can't return to a prior set). Keyed on
    // the current passage id: stable while navigating within the set, changes
    // when advancing to the next set.
    useEffect(() => {
        setBookmarkedQuestions(new Set());
    }, [lockCurPassageId]);
    // Back is allowed only when the previous question is in the SAME
    // (non-null) passage. First-of-passage, standalone (Math, no passage),
    // and module-start all disable back so you can't return to a prior
    // passage. Only active when lockNavigation is on.
    const lockedBackDisabled = (() => {
        if (!lockNavigation) return false;
        if (lockCurIdx <= 0) return true;
        const prevPassageId = lockOrderedQuestions[lockCurIdx - 1]?.passages?.id ?? null;
        return !(lockCurPassageId != null && lockCurPassageId === prevPassageId);
    })();
    // True when advancing forward will leave the current passage (current
    // is the last question of its passage group). Drives the end-of-
    // passage interstitial, mirroring the adaptive flow.
    const lockedIsLastInPassage = (() => {
        if (!lockNavigation || lockCurIdx < 0 || lockCurPassageId == null) return false;
        const nextPassageId = lockOrderedQuestions[lockCurIdx + 1]?.passages?.id ?? null;
        return nextPassageId !== lockCurPassageId; // next is a new passage (or none)
    })();
    // ---- ELA unified "Stand alone" block (Revising/Editing Part A + Part B) ----
    // ONE block = everything after the reading passages through the ELA section
    // end (computeStandaloneBlocks starts at readingEndIndex, so it INCLUDES
    // Part A + Part B). Only computed for the ELA module, so Math never shows a
    // stand-alone START. The single "Stand alone items" START shows once, at
    // the block's first question, in BOTH locked and unlocked flows, and BEFORE
    // the sub-section directions (which are sub-steps shown after the START —
    // see the ``!standaloneStartPending`` gate on showSectionDirections below).
    const standaloneBlocks = currentModule?.subject === 'English Language Arts'
        ? computeStandaloneBlocks(currentModule?.questions ?? [])
        : [];
    const currentStandaloneBlock = questionNumber !== 0
        ? findStandaloneBlock(standaloneBlocks, questionNumber)
        : null;
    const standaloneStartPending =
        !start &&
        currentStandaloneBlock != null &&
        currentStandaloneBlock.positionInBlock === 1 &&
        !standaloneStartAck.has(currentStandaloneBlock.firstQuestionNum) &&
        !showReadingDirections &&
        !showPassageInterstitial;
    // Section-directions interstitial (Revising/Editing Part A / Part B). Shown
    // BEFORE a question whose workbook marker resolved to a directions prompt,
    // once per question. Never co-shown with the end-of-passage interstitial,
    // and AFTER the stand-alone START (the START is the section entry; the Part
    // A / Part B directions are sub-steps that follow it).
    const currentDirectionsPrompt = resolveSectionDirections(currentQuestion?.sectionDirections);
    const showSectionDirections =
        !!currentDirectionsPrompt &&
        currentQuestion != null &&
        questionNumber !== 0 &&
        !directionsAck.has(questionNumber) &&
        !standaloneStartPending &&
        !showReadingDirections &&
        !showPassageInterstitial;
    const acknowledgeDirections = () => {
        setDirectionsAck((prev) => {
            const next = new Set(prev);
            next.add(questionNumber);
            return next;
        });
    };
    // ---- ELA passage sets (official TestNav framing) ----
    // Contiguous groups of the current module's questions sharing a
    // passage. Drives the "ELA – PASSAGE SET X OF N" top-bar label, the
    // per-set "n OF n" question count, and the per-set START screen.
    // Math (and any standalone ELA question) has no set -> null -> the
    // existing global header treatment is used.
    const passageSets = currentModule?.subject === 'English Language Arts'
        ? computePassageSets(currentModule?.questions ?? [])
        : [];
    const currentPassageSet = questionNumber !== 0
        ? findPassageSet(passageSets, questionNumber)
        : null;
    // Per-passage-set START screen (counterpart of the end-of-passage
    // interstitial), shown BEFORE each ELA passage set including the
    // first — i.e. whenever the student lands on a set's first question
    // and hasn't dismissed that set's start screen yet. Shown once per set
    // in BOTH locked and unlocked flows (the ack Set prevents re-showing on
    // revisit / free review); section directions take precedence.
    const showPassageSetStart =
        !start &&
        currentPassageSet != null &&
        currentPassageSet.positionInSet === 1 &&
        !passageStartAck.has(currentPassageSet.passageId) &&
        !showSectionDirections &&
        !showReadingDirections &&
        !showPassageInterstitial;
    const acknowledgePassageSetStart = () => {
        if (!currentPassageSet) return;
        const passageId = currentPassageSet.passageId;
        setPassageStartAck((prev) => {
            const next = new Set(prev);
            next.add(passageId);
            return next;
        });
    };
    // The single "Stand alone items" START (computed above as
    // standaloneStartPending, so it can gate the directions to appear after it).
    const showStandaloneStart = standaloneStartPending;
    const acknowledgeStandaloneStart = () => {
        if (!currentStandaloneBlock) return;
        const firstQuestionNum = currentStandaloneBlock.firstQuestionNum;
        setStandaloneStartAck((prev) => {
            const next = new Set(prev);
            next.add(firstQuestionNum);
            return next;
        });
    };
    // Revising/Editing Part A is now part of the unified stand-alone block
    // above (no separate Part A START / label). Its shared passage still
    // renders and keeps within-group nav (driven by passages.id, unchanged),
    // and its marker still drives the Part A directions screen.
    // ---- End-of-section screen ----
    // The flow index of the current module (modules is FLOW-ordered), and
    // whether it's the last question of the current module. Used to insert
    // an end-of-section screen at a NON-final section boundary.
    const currentModuleFlowIndex = modules?.findIndex((m) => m.id === currentModule?.id) ?? -1;
    const isFinalModule = currentModuleFlowIndex === (modules?.length ?? 0) - 1;
    const currentModuleQuestions = currentModule?.questions ?? [];
    const lastModuleQuestion = currentModuleQuestions.length
        ? currentModuleQuestions[currentModuleQuestions.length - 1]
        : null;
    const moduleLastQuestionNum = lastModuleQuestion?.questionNum ?? null;
    const isLastQuestionOfModule =
        moduleLastQuestionNum != null && questionNumber === moduleLastQuestionNum;
    // The ELA section ends on the stand-alone (Part B) block, so its end
    // screen reads "End of ELA – Stand alone items". Marker-based: the module
    // is ELA AND has a Part B block (``computeStandaloneBlocks`` non-empty).
    // Part B items commonly carry a passage, so the old null-passage check on
    // the last question was unreliable.
    const moduleEndsOnStandalone =
        isElaSection(currentModule?.subject) && standaloneBlocks.length > 0;
    // Whether every question in the current section has a non-empty answer
    // (drives the green "All Questions Answered" line). Locked flows enforce
    // no-skip, so this is normally true at the boundary. Self-contained
    // (mirrors isLockQuestionAnswered, which is declared later) to avoid a
    // temporal-dead-zone reference.
    const moduleAllAnswered = !!currentModule?.questions?.every((q) => {
        const answerObj = currentModule?.answers?.find((a) => a.questionNum === q.questionNum);
        if (!answerObj) return false;
        const ans = answerObj.answer;
        const unanswered =
            ans === null || ans === '' || ans.length === 0 ||
            ((Array.isArray(ans) && Array.isArray(ans[0])) &&
                (ans as [number, string][]).filter(([, val]) => val !== '').length === 0);
        return !unanswered;
    });
    // Questions in the passage the student is currently on — the ONLY
    // ones the passage-scoped Review may jump to when navigation is
    // locked (they may move between this passage's questions and nowhere
    // else). Empty for standalone (Math) questions / when not locked.
    const lockPassageQuestions = (lockNavigation && lockCurPassageId != null)
        ? lockOrderedQuestions.filter((q) => (q.passages?.id ?? null) === lockCurPassageId)
        : [];
    // Suppress the end-of-passage interstitial for SINGLE-question stand-alone
    // items, mirroring adaptive's ``inStandaloneSingle`` rule (_navigabilityFor):
    // inside the ELA unified stand-alone block, an item that is null-passage OR
    // whose passage group has <= 1 question does NOT show the "end of passage"
    // screen. A MULTI-question shared passage INSIDE the block (Part A) is NOT
    // suppressed — it keeps the interstitial at its last question, exactly like
    // adaptive keeps the passage branch for such groups. Reading-comprehension
    // passage sets (before the block) are unaffected. ``lockPassageQuestions``
    // is the current passage group (empty for null-passage), so ``<= 1`` covers
    // both null-passage and single-question-passage stand-alone items.
    const inStandaloneSingleItem =
        currentStandaloneBlock != null && lockPassageQuestions.length <= 1;
    // A question is "answered" when its saved answer is non-empty. Mirrors
    // the navigator's unanswered test (handles MC, text, and the nested
    // [index, value] inline/fill-in shape).
    const isLockQuestionAnswered = (questionNum: number) => {
        const answerObj = currentModule?.answers?.find((a) => a.questionNum === questionNum);
        if (!answerObj) return false;
        const ans = answerObj.answer;
        const unanswered =
            ans === null || ans === '' || ans.length === 0 ||
            ((Array.isArray(ans) && Array.isArray(ans[0])) &&
                (ans as [number, string][]).filter(([, val]) => val !== '').length === 0);
        return !unanswered;
    };
    // No-skip rule for locked tests: you may only move forward once the
    // current question is answered (matches the adaptive flow). Only
    // gated on real questions — the intro (questionNumber 0) and the
    // end-of-section screen are not.
    const lockedForwardDisabled =
        lockNavigation && lockCurIdx >= 0 &&
        !isLockQuestionAnswered(questionNumber) && !lockLiveAnswered;

    // ---- Per-passage-set Review + Bookmark (locked ELA passage sets) ----
    // Shown for a MULTI-question passage set (reading OR Part A). Math (no
    // passage) and single-question stand-alone items (length <= 1) get neither.
    const showPassageTools = lockNavigation && lockPassageQuestions.length > 1;
    const isCurrentBookmarked = bookmarkedQuestions.has(questionNumber);
    const toggleBookmark = () => {
        setBookmarkedQuestions((prev) => {
            const next = new Set(prev);
            if (next.has(questionNumber)) next.delete(questionNumber);
            else next.add(questionNumber);
            return next;
        });
    };
    // Review-list entries for the current passage set: the set's question rows
    // and (once reached) the clickable "Passage End Directions" / "End of
    // Section" end rows. No leading directions row.
    const moduleQuestionsForReview = currentModule?.questions ?? [];
    const isLastPassageSetOfSection =
        lockPassageQuestions.length > 0 &&
        moduleQuestionsForReview.length > 0 &&
        lockPassageQuestions[lockPassageQuestions.length - 1].questionNum
            === moduleQuestionsForReview[moduleQuestionsForReview.length - 1].questionNum;
    // The passage-end ("Passage End Directions") page is REACHED once every
    // question of the set is answered (i.e. the end-of-passage interstitial is
    // available). Until then it — and the "End of Section" row — aren't listed,
    // mirroring the reached-questions gating on the question rows.
    const passageEndReached =
        lockPassageQuestions.length > 0 &&
        lockPassageQuestions.every((q) => isLockQuestionAnswered(q.questionNum));
    const passageReviewEntries: PassageReviewEntry[] = [
        // Leading "ELA Rdg Comp Directions" row — ONLY on the FIRST reading
        // passage set (the reading directions are presented once, before the
        // first passage). Clickable → the reading-comp directions overlay.
        // Not shown on later reading sets, and never for Part A/B.
        ...(currentPassageSet?.setIndex === 1
            ? [{
                key: -4,
                label: 'ELA Rdg Comp Directions',
                kind: 'directions' as const,
                target: 'reading-directions' as const,
                isCurrent: showReadingDirections,
            }]
            : []),
        // Question rows, numbered per-set (1..N within THIS passage set), not by
        // global question number. Only REACHED questions (answered, or the
        // current one) are listed — mirrors the adaptive flow (served slots
        // only) and keeps no-skip intact. At the end-of-passage interstitial
        // all are answered, so all show. The jump target stays the global
        // questionNum; only the displayed label is the per-set position.
        ...lockPassageQuestions
            .map((q, idx) => ({ q, position: idx + 1 }))
            .filter(({ q }) => isLockQuestionAnswered(q.questionNum) || q.questionNum === questionNumber)
            .map(({ q, position }) => ({
                key: q.questionNum,
                label: `Question ${position}`,
                kind: 'question' as const,
                answered: isLockQuestionAnswered(q.questionNum),
                bookmarked: bookmarkedQuestions.has(q.questionNum),
                isCurrent: q.questionNum === questionNumber,
            })),
        // Clickable once reached: jumps to the end-of-passage interstitial /
        // (last set) the end-of-section screen. Highlighted when on that screen.
        ...(passageEndReached
            ? [{
                key: -2,
                label: 'Passage End Directions',
                kind: 'end' as const,
                target: 'passage-end' as const,
                isCurrent: showPassageInterstitial,
            }]
            : []),
        ...(passageEndReached && isLastPassageSetOfSection
            ? [{
                key: -3,
                label: 'End of Section',
                kind: 'end' as const,
                target: 'end-of-section' as const,
                isCurrent: showSectionEnd,
            }]
            : []),
    ];

    // Highest question number across the whole test — the "N OF M" header
    // total. Independent of flow order (the flat list's LAST entry is the
    // chosen-last section, not necessarily the highest-numbered one).
    const lastGlobalQuestionNum = Math.max(
        0,
        ...(modules ?? []).map(m => m.questions[m.questions.length - 1]?.questionNum ?? 0),
    );

    // Section-level "N OF M" header count — the WITHIN-CURRENT-SECTION position
    // (1..this section's size), not the global question number. Matches the
    // adaptive flow ("MATH / 1 OF 50" regardless of ELA/Math order). Used only
    // for the non-passage / non-standalone fallback branch below; passage-set
    // and stand-alone counts are already sub-section counts. Reuses the
    // module's question list (currentModuleQuestions), ordered by questionNum.
    const sectionOrderedQuestions = [...currentModuleQuestions].sort(
        (a, b) => a.questionNum - b.questionNum,
    );
    const sectionPosition = sectionOrderedQuestions.findIndex(
        (q) => q.questionNum === questionNumber,
    ) + 1;
    const sectionTotal = sectionOrderedQuestions.length;

    return (
        <div id='digitalSHSATModule' className='digitalSHSATModule'>
            {/* The "Finish this passage?" confirm modal was replaced by the
                end-of-passage interstitial screen (PassageInterstitial,
                page 11 of the printed test), rendered in the body below. */}
            {loading && loadingPopup}
            {isExitOpen && exitPopup}
            {/* Notepad*/}
            <div id='notepadDrag' className={toolsSelected === 'Global Notepad'
                ? 'digitalSHSATModule__popup--notepad'
                : 'digitalSHSATModule__popup--hidden'
            }>
                <div id='notepadHeader' className='digitalSHSATModule__popupHeader draggable-element'>
                    <h3 className='digitalSHSATModule__popupHeaderText'>Global Notepad</h3>
                    <IoClose className='digitalSHSATModule__popupHeaderClose' onClick={() => handleTools('Global Notepad')} />
                </div>
                <div className='digitalSHSATModule__popupNotepad'>
                    <textarea 
                        className='digitalSHSATModule__popupNotepadInput'
                        value={notes}
                        onChange={(e) => saveNotes(e.target.value)}
                    >
                    </textarea>
                </div>
            </div>
            <div id='definitionDrag' className={currentDefinition
                ? 'digitalSHSATModule__popup--definition'
                : 'digitalSHSATModule__popup--hidden'
            }
                style={{
                    position: 'absolute',
                    top: popupPos.top,
                    left: popupPos.left
                }}
            >
                <div id='definitionHeader' className='digitalSHSATModule__popupHeader draggable-element'>
                    <h3 className='digitalSHSATModule__popupHeaderText'>Glossary Definition</h3>
                    <IoClose className='digitalSHSATModule__popupHeaderCloseBig' onClick={()=> setCurrentDefinition(null)} />
                </div>
                <div className='digitalSHSATModule__popupDefinition'>
                    {currentDefinition?.definition}
                </div>
            </div>
            {/* Choice Count limit popup */}
            <div className={`digitalSHSATModule__alertPopup ${isAlertPopupOpen ? 'show' : 'hide'}`}>
                <div>You are permitted a <span className='digitalSHSATModule__bold'>
                    maximum of{'  '}
                    {(currentQuestion?.questionType===3 || currentQuestion?.questionType===10)
                    ? currentModule?.questions?.find((question)=> question.questionNum === currentQuestion?.questionNum)?.choices.filter((choice)=> choice.isCorrect).length
                    : currentModule?.questions?.find((question)=> question.questionNum === currentQuestion?.questionNum)?.hotText.filter((ht)=> ht.isCorrect).length} 
                    {'  '}choices</span> for this question.<br /> Please unselect one of your choices before making another choice.
                </div>
                <div className='digitalSHSATModule__alertPopupCloseIcon' onClick={() => handlePopup(false)}><IoClose /></div>
            </div>
            {/* header */}
            <div className='digitalSHSATModule__header'>
                <div className='digitalSHSATModule__headerTop'>
                    <div className='digitalSHSATModule__headerBox'>
                        {/* header - left */}
                        {!start 
                        ? <div className='digitalSHSATModule__headerLeft'>
                            <div className='digitalSHSATModule__arrowButtons'>
                                <div className="digitalSHSATModule__buttonWithTooltip">
                                    <button className='digitalSHSATModule__arrowButton'
                                        disabled={(questionNumber === 0 && modules?.findIndex((module) => module === currentModule) === 0) || lockedBackDisabled}
                                        onClick={() => {
                                            // Reading-comp directions overlay: back dismisses it.
                                            if (showReadingDirections) {
                                                setShowReadingDirections(false);
                                                return;
                                            }
                                            // End-of-section screen: back dismisses it
                                            // and returns to the section's last question.
                                            if (showSectionEnd) {
                                                setShowSectionEnd(false);
                                                return;
                                            }
                                            // Back from the interstitial re-enters the passage.
                                            setShowPassageInterstitial(false);
                                            // From the END screen, step back to the last question
                                            // of the FLOW-last module. (questionNumber - 1 assumed
                                            // the flow-last module is also the highest-numbered
                                            // one, which breaks for a Math-first section order.)
                                            const atEndScreen = questionNumber !== 0 &&
                                                !modules?.find(m => m.questions.find(q => q.questionNum === questionNumber));
                                            if (atEndScreen && modules?.length > 1) {
                                                const flowLastModule = modules[modules.length - 1];
                                                navigateQuestion(
                                                    flowLastModule.questions[flowLastModule.questions.length - 1].questionNum,
                                                    flowLastModule.id,
                                                );
                                                return;
                                            }
                                            navigateQuestion(questionNumber - 1, currentModule.id);
                                        }}
                                    >
                                        <FaArrowLeft />
                                    </button>
                                    {!(questionNumber === 0 && modules?.findIndex((module) => module === currentModule) === 0) && <span className="digitalSHSATModule__tooltip">Previous</span>}
                                </div>
                                <div className="digitalSHSATModule__buttonWithTooltip">
                                    <button className='digitalSHSATModule__arrowButton'
                                        disabled={!showSectionDirections && !showPassageSetStart && !showStandaloneStart && !showReadingDirections && (showSectionEnd || ((questionNumber !== 0 && !modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber))) || lockedForwardDisabled))}
                                        onClick={() => {
                                            // Reading-comp directions overlay showing:
                                            // the blue arrow dismisses it (reveals the
                                            // question again), it does not advance.
                                            if (showReadingDirections) {
                                                setShowReadingDirections(false);
                                                return;
                                            }
                                            // End-of-section screen showing: its own
                                            // Continue button drives the flow; the
                                            // top arrow is inert (disabled above).
                                            if (showSectionEnd) return;
                                            // Section directions showing: the blue
                                            // arrow acknowledges (reveals the
                                            // question), it does not advance. Must
                                            // run before the no-skip guard since the
                                            // question is still unanswered here.
                                            if (showSectionDirections) {
                                                acknowledgeDirections();
                                                return;
                                            }
                                            // Stand-alone START showing: the blue
                                            // arrow doubles as its Start button.
                                            // Ordered after directions so the
                                            // sub-section directions show first
                                            // (directions → START → first question).
                                            if (showStandaloneStart) {
                                                acknowledgeStandaloneStart();
                                                return;
                                            }
                                            // Passage-set START screen showing: the
                                            // blue arrow doubles as its Start button
                                            // (reveals the set's first question).
                                            if (showPassageSetStart) {
                                                acknowledgePassageSetStart();
                                                return;
                                            }
                                            // No-skip when locked: the disabled
                                            // attr already blocks an unanswered
                                            // forward, but guard here too.
                                            if (lockedForwardDisabled) return;
                                            // Advancing PAST the last question of a
                                            // NON-final section shows the end-of-
                                            // section screen instead of crossing the
                                            // boundary immediately (locked flows). Its
                                            // Continue button then does the crossing.
                                            const crossingNonFinalBoundary =
                                                lockNavigation && isLastQuestionOfModule && !isFinalModule;
                                            // Locked navigation, end of a passage set:
                                            //  - if the interstitial is showing, the blue
                                            //    forward arrow advances + locks the passage;
                                            //  - otherwise (still on the last question) show
                                            //    the interstitial first.
                                            // Unlocked or mid-passage: advance normally.
                                            if (lockNavigation && showPassageInterstitial) {
                                                setShowPassageInterstitial(false);
                                                // Leaving the last passage of a non-final
                                                // section -> end-of-section screen.
                                                if (crossingNonFinalBoundary) {
                                                    setShowSectionEnd(true);
                                                    return;
                                                }
                                                navigateQuestion(questionNumber + 1, currentModule.id);
                                            } else if (lockNavigation && lockedIsLastInPassage && !inStandaloneSingleItem) {
                                                // End of a (reading, or Part A shared)
                                                // passage → show the interstitial. Single-
                                                // question stand-alone items are suppressed
                                                // (inStandaloneSingleItem), matching adaptive,
                                                // so they advance straight to the next item.
                                                setShowPassageInterstitial(true);
                                            } else if (crossingNonFinalBoundary) {
                                                // Non-passage last question (stand-alone /
                                                // Math) of a non-final section.
                                                setShowSectionEnd(true);
                                            } else {
                                                navigateQuestion(questionNumber + 1, currentModule.id);
                                            }
                                        }}
                                    >
                                        <FaArrowRight />
                                    </button>
                                    {questionNumber <= modules?.map(m => m.questions).flat().length && <span className="digitalSHSATModule__tooltip">Next</span>}
                                </div>
                            </div>
                            <div className='digitalSHSATModule__reviews'>
                                {/* Locked navigation: per-passage-set Review (tabbed) +
                                    Bookmark, shown DURING the passage set (and on its
                                    end-of-passage interstitial) for multi-question ELA
                                    sets (reading OR Part A). Scoped to the current set;
                                    Math / single-question items get neither. Bookmarks
                                    are ephemeral and reset when moving to the next set. */}
                                {showPassageTools &&
                                    <div className='digitalSHSATModule__review' ref={reviewOpenRef}>
                                        <div className="digitalSHSATModule__buttonWithTooltip">
                                            <div className={isReviewOpen
                                                ? 'digitalSHSATModule__reviewBox--active'
                                                : 'digitalSHSATModule__reviewBox'
                                                }
                                                onClick={() => { handleReviewOpen(); setPassageReviewTab('All Questions'); }}
                                            >
                                                Review
                                                <PiListBulletsBold />
                                            </div>
                                            <span className="digitalSHSATModule__tooltip">Review</span>
                                        </div>
                                        {isReviewOpen &&
                                            <PassageReviewList
                                                entries={passageReviewEntries}
                                                activeTab={passageReviewTab}
                                                onTabChange={setPassageReviewTab}
                                                onJump={(entry) => {
                                                    closeReviewNavigator();
                                                    if (entry.target === 'reading-directions') {
                                                        // Show the reading-comp directions overlay.
                                                        setShowSectionEnd(false);
                                                        setShowPassageInterstitial(false);
                                                        setShowReadingDirections(true);
                                                        return;
                                                    }
                                                    setShowReadingDirections(false);
                                                    if (entry.kind === 'question') {
                                                        // Jump to a question within the set.
                                                        setShowSectionEnd(false);
                                                        setShowPassageInterstitial(false);
                                                        if (entry.key !== questionNumber) {
                                                            navigateQuestion(entry.key, currentModule.id);
                                                        }
                                                    } else if (entry.target === 'passage-end') {
                                                        // Show the end-of-passage interstitial: sit on the
                                                        // set's last question, then arm the interstitial.
                                                        setShowSectionEnd(false);
                                                        const lastQ = lockPassageQuestions[lockPassageQuestions.length - 1];
                                                        if (lastQ && lastQ.questionNum !== questionNumber) {
                                                            navigateQuestion(lastQ.questionNum, currentModule.id);
                                                        }
                                                        setShowPassageInterstitial(true);
                                                    } else if (entry.target === 'end-of-section') {
                                                        // Advance to the end-of-section screen (last set only).
                                                        setShowPassageInterstitial(false);
                                                        if (isFinalModule) {
                                                            // Final section -> the terminal end screen.
                                                            if (moduleLastQuestionNum != null) {
                                                                navigateQuestion(moduleLastQuestionNum + 1, currentModule.id);
                                                            }
                                                        } else {
                                                            // Non-final -> "End of {section}" (Continue → next).
                                                            if (moduleLastQuestionNum != null && questionNumber !== moduleLastQuestionNum) {
                                                                navigateQuestion(moduleLastQuestionNum, currentModule.id);
                                                            }
                                                            setShowSectionEnd(true);
                                                        }
                                                    }
                                                }}
                                            />
                                        }
                                    </div>
                                }
                                {/* Bookmark (locked ELA passage set) — ephemeral, per-set.
                                    Toggles the current question's bookmark. */}
                                {showPassageTools &&
                                    <div className="digitalSHSATModule__buttonWithTooltip">
                                        <div className={isCurrentBookmarked
                                            ? 'digitalSHSATModule__reviewBox--active'
                                            : 'digitalSHSATModule__reviewBox'}
                                            onClick={toggleBookmark}
                                        >
                                            {isCurrentBookmarked
                                                ? <FaBookmark className='digitalSHSATModule__reviewBox--activeIcon' />
                                                : <FaRegBookmark />}
                                            Bookmark
                                        </div>
                                        <span className="digitalSHSATModule__tooltip">Bookmark Question for Review</span>
                                    </div>
                                }
                                {/* Review + question navigator let you jump to any
                                    question — hidden when navigation is locked so the
                                    only way through the test is the gated arrows. */}
                                {!lockNavigation && <div className='digitalSHSATModule__review' ref={reviewOpenRef}>
                                    <div className="digitalSHSATModule__buttonWithTooltip">
                                        <div className={isReviewOpen
                                            ? 'digitalSHSATModule__reviewBox--active'
                                            : 'digitalSHSATModule__reviewBox'
                                            } 
                                            onClick={handleReviewOpen}
                                        >
                                            Review
                                            <PiListBulletsBold />
                                        </div>
                                        <span className="digitalSHSATModule__tooltip">Review</span>
                                    </div>
                                    {(isReviewOpen && modules) &&
                                        <div className='digitalSHSATModule__reviewNavigator'>
                                            <div className='digitalSHSATModule__navigatorHeader'>
                                                {NAVIGATORFILTER.map(filter => 
                                                    <div className={navigatorFilter===filter
                                                        ? 'digitalSHSATModule__navigatorHeaderFilter--active' 
                                                        : 'digitalSHSATModule__navigatorHeaderFilter'}
                                                        onClick={() => setNavigatorFilter(filter)}
                                                    >
                                                        {filter==='All Questions' &&
                                                            <div className='digitalSHSATModule__navigatorIconRec'>
                                                                {modules.map(m => m.questions).flat().length}
                                                            </div>
                                                        }
                                                        {filter==='Not Answered' &&
                                                            <div className='digitalSHSATModule__navigatorIconCir'>
                                                                {modules.flatMap(module => module.questions).filter(q => modules.flatMap(module => module.answers).find(a => a.questionNum === q.questionNum && (a.answer === '' || a.answer === null || a.answer.length === 0 || ((Array.isArray(a.answer) && Array.isArray(a.answer[0]))&& (a.answer as [number, string][]).filter(( [, val]) => val !== "").length === 0)))).length}
                                                            </div>
                                                        }                                                        
                                                        {filter==='Bookmarks' &&
                                                            <div className='digitalSHSATModule__navigatorIconMark'>
                                                                <FaBookmark />
                                                                <div className='digitalSHSATModule__navigatorIconMarkContent'>
                                                                    {modules.flatMap(module => module.questions).filter(q => modules.flatMap(module => module.answers).find(a => a.questionNum === q.questionNum && a.marked)).length}
                                                                </div>
                                                            </div>
                                                        }
                                                        <span>{filter}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className='digitalSHSATModule__navigatorBody'>
                                                <div className='digitalSHSATModule__navigatorBodyBox' ref={navigatorContainerRef}>
                                                    {(navigatorFilter === 'All Questions' && modules?.find((module) => module.subject === 'English Language Arts')) &&
                                                        <div className={questionNumber === 0 && currentModule === modules?.find((module) => module.subject === 'English Language Arts')? 'digitalSHSATModule__navigatorBodyContent--active' : 'digitalSHSATModule__navigatorBodyContent'} 
                                                            onClick={() => navigateQuestion(0, modules?.find((module) => module.subject === 'English Language Arts')?.id)}
                                                            ref={el => { subjectRefs.current['English Language Arts'] = el; }}
                                                        >
                                                            <div className='digitalSHSATModule__navigatorQuestionNone' />
                                                            English Language Arts
                                                        </div>
                                                    }
                                                    {filteredQuestions?.map(question => 
                                                        question.subject ==='English Language Arts' && 
                                                        <div className={questionNumber === question.questionNum? 'digitalSHSATModule__navigatorBodyContent--active' : 'digitalSHSATModule__navigatorBodyContent'}
                                                            onClick={() => navigateQuestion(question.questionNum, modules?.find((module) => module.subject === 'English Language Arts')?.id)}
                                                            ref={el => { questionRefs.current[question.questionNum] = el }}
                                                        >
                                                            {(() => {
                                                                const answerObj = modules?.find((module) => module.subject === 'English Language Arts')?.answers?.find(answer => answer.questionNum === question.questionNum);
                                                                const isUnanswered = answerObj?.answer === null || answerObj?.answer === '' || answerObj?.answer.length === 0 || ((Array.isArray(answerObj.answer) && Array.isArray(answerObj.answer[0]))&& (answerObj.answer as [number, string][]).filter(( [, val]) => val !== "").length === 0);
                                                                return isUnanswered ? (
                                                                    <div
                                                                        className={
                                                                            questionNumber === question.questionNum
                                                                                ? 'digitalSHSATModule__navigatorQuestionCir--active'
                                                                                : 'digitalSHSATModule__navigatorQuestionCir'
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <div className="digitalSHSATModule__navigatorQuestionNone" />
                                                                );
                                                            })()}
                                                            Question {question.questionNum}
                                                            {modules?.find((module) => module.subject === 'English Language Arts')?.answers?.find(answer => answer.questionNum===question.questionNum)?.marked && <div className='digitalSHSATModule__navigatorBookmark'>
                                                                <FaBookmark className='digitalSHSATModule__reviewBox--activeIcon'/>
                                                            </div>}
                                                        </div>
                                                    )}
                                                    {(navigatorFilter === 'All Questions' && modules?.find((module) => module.subject === 'Mathematics')) &&
                                                        <div className={questionNumber === 0 && currentModule === modules?.find((module) => module.subject === 'Mathematics')? 'digitalSHSATModule__navigatorBodyContent--active' : 'digitalSHSATModule__navigatorBodyContent'} 
                                                            onClick={() => navigateQuestion(0, modules?.find((module) => module.subject === 'Mathematics')?.id)}
                                                            ref={el => { subjectRefs.current['Mathematics'] = el; }}
                                                        >
                                                            <div className='digitalSHSATModule__navigatorQuestionNone' />
                                                            Mathematics
                                                        </div>
                                                    }
                                                    {filteredQuestions?.map(question => 
                                                        question.subject ==='Mathematics' && 
                                                        <div className={questionNumber === question.questionNum? 'digitalSHSATModule__navigatorBodyContent--active' : 'digitalSHSATModule__navigatorBodyContent'}
                                                            onClick={() => navigateQuestion(question.questionNum, modules?.find((module) => module.subject === 'Mathematics')?.id)}
                                                            ref={el => { questionRefs.current[question.questionNum] = el }}
                                                        >
                                                            {(() => {
                                                                const answerObj = modules?.find((module) => module.subject === 'Mathematics')?.answers?.find(answer => answer.questionNum === question.questionNum);
                                                                const isUnanswered = answerObj?.answer === null || answerObj?.answer === '' || answerObj?.answer.length === 0 || ((Array.isArray(answerObj.answer) && Array.isArray(answerObj.answer[0]))&& (answerObj.answer as [number, string][]).filter(( [, val]) => val !== "").length === 0);
                                                                return isUnanswered ? (
                                                                    <div
                                                                        className={
                                                                            questionNumber === question.questionNum
                                                                                ? 'digitalSHSATModule__navigatorQuestionCir--active'
                                                                                : 'digitalSHSATModule__navigatorQuestionCir'
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <div className="digitalSHSATModule__navigatorQuestionNone" />
                                                                );
                                                            })()}
                                                            Question {question.questionNum}
                                                            {modules?.find((module) => module.subject === 'Mathematics')?.answers?.find(answer => answer.questionNum===question.questionNum)?.marked && <div className='digitalSHSATModule__navigatorBookmark'>
                                                                <FaBookmark className='digitalSHSATModule__reviewBox--activeIcon'/>
                                                            </div>}
                                                        </div>                                                    
                                                    )}
                                                    {navigatorFilter === 'All Questions' && 
                                                        <div className={(questionNumber!==0 && !modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber)))? 'digitalSHSATModule__navigatorBodyContent--active' : 'digitalSHSATModule__navigatorBodyContent'} 
                                                            onClick={() => navigateQuestion(modules?.map(m => m.questions).flat()[modules?.map(m => m.questions).flat().length-1].questionNum + 1, modules[modules.length-1].id)}
                                                            ref={el => { subjectRefs.current['End Test'] = el; }}
                                                        >
                                                            <div className='digitalSHSATModule__navigatorQuestionNone' />
                                                            End of Section
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>}
                                {/* Bookmark is hidden when locked — with no Review
                                    screen there's nothing to do with bookmarks. */}
                                {(!lockNavigation && !start && modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber))) &&
                                    (currentModule?.answers?.find(answer => answer.questionNum===questionNumber)?.marked
                                    ? <div className="digitalSHSATModule__buttonWithTooltip">
                                        <div className='digitalSHSATModule__reviewBox--active' onClick={() => markQuestion(questionNumber)}>
                                            <FaBookmark className='digitalSHSATModule__reviewBox--activeIcon'/>
                                            Bookmark
                                        </div>
                                        <span className="digitalSHSATModule__tooltip">Bookmark Question for Review</span>
                                    </div>
                                    : <div className="digitalSHSATModule__buttonWithTooltip">
                                        <div className='digitalSHSATModule__reviewBox' onClick={() => markQuestion(questionNumber)}>
                                            <FaRegBookmark />
                                            Bookmark
                                        </div>
                                        <span className="digitalSHSATModule__tooltip">Bookmark Question for Review</span>
                                    </div>
                                    )
                                }
                            </div>
                            {modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber)) &&
                                <div className='digitalSHSATModule__functionGroup'>
                                    <div className="digitalSHSATModule__buttonWithTooltip">
                                        <button className={toolsSelected === 'Pointer'
                                            ? 'digitalSHSATModule__functionButton--active digitalSHSATModule__leftBorderRadius'
                                            : 'digitalSHSATModule__functionButton'
                                            }
                                            onClick={()=> handleTools('Pointer')}
                                        >
                                            <FaArrowPointer className='icon-margin' />
                                        </button>
                                        <span className="digitalSHSATModule__tooltip">Pointer</span>
                                    </div>
                                    <div className="digitalSHSATModule__buttonWithTooltip">
                                        <button className={(questionNumber <=0 || (currentQuestion?.questionType !== 1 && currentQuestion?.questionType !== 3))
                                            ? 'digitalSHSATModule__functionButton--disabled'
                                            : toolsSelected === 'Answer Eliminator'
                                            ? 'digitalSHSATModule__functionButton--active'
                                            : 'digitalSHSATModule__functionButton'
                                            }
                                            onClick={questionNumber > 0 ? () => handleTools('Answer Eliminator') : undefined}
                                        >
                                            <RxCross2 className='icon-margin'/>
                                        </button>
                                        <span className="digitalSHSATModule__tooltip">Answer Eliminator</span>
                                    </div>
                                    <div className="digitalSHSATModule__buttonWithTooltip">
                                        <button className={toolsSelected === 'Global Notepad'
                                            ? 'digitalSHSATModule__functionButton--active digitalSHSATModule__rightBorderRadius'
                                            : 'digitalSHSATModule__functionButton'
                                            }
                                            onClick={()=> handleTools('Global Notepad')}
                                        >
                                            <PiNote className='icon-margin'/>
                                        </button>
                                        <span className="digitalSHSATModule__tooltip">Global Notepad</span>
                                    </div>
                                </div>
                            }
                        </div>
                        :
                        <div className='digitalSHSATModule__headerRightTestName'>{testName}{modules.length===1&& `- ${currentModule?.subject} Only`}</div>
                        }
                        {/* header - right */}
                        <div className='digitalSHSATModule__headerRight' ref={dropdwonOpenRef}>
                            <div className='digitalSHSATModule__timer'>
                                {isShowTimer && <div className='digitalSHSATModule__timing'>
                                    {isUntimed? 'Untimed' : formatTime(timeLeft)}
                                </div>}
                                <div className="digitalSHSATModule__buttonWithTooltip">
                                    <button className={isShowTimer? 'digitalSHSATModule__timerButton--active' : 'digitalSHSATModule__timerButton'} onClick={() => setIsShowTimer(!isShowTimer)}>
                                        <TfiTimer />
                                    </button>
                                    <span className="digitalSHSATModule__tooltip">{isShowTimer? 'Hide Timer' : 'Show Timer'}</span>
                                </div>
                            </div>
                            <div className='digitalSHSATModule__guest'>
                                {studentName}
                            </div>
                            <div className="digitalSHSATModule__buttonWithTooltip">
                                <div className={isDropdownOpen? 'digitalSHSATModule__guestButton--active' : 'digitalSHSATModule__guestButton'} 
                                    onClick={()=> handleDropdownToolsOpen()}>
                                    <IoMdPerson />
                                    <IoMdArrowDropdown className='digitalSHSATModule__profileArrow'/>
                                </div>
                                <span className="digitalSHSATModule__tooltip">User dropdown menu</span>
                            </div>
                            {isDropdownOpen &&
                                <div className='digitalSHSATModule__dropdown'>
                                    {questionNumber <= modules?.map(m => m.questions).flat().length &&
                                        <div className='digitalSHSATModule__dropdownTools'>
                                            {/* Contrast Settings — opens the
                                                container-owned theme modal (not a
                                                toggle). Ordered first to match the
                                                official TestNav tools menu. */}
                                            {onOpenContrast && (
                                                <div className='digitalSHSATModule__dropdownContent'
                                                    onClick={() => { setIsDropdownOpen(false); onOpenContrast(); }}
                                                >
                                                    Change the background and foreground color
                                                </div>
                                            )}
                                            {DROPDOWNTOOLS.map((tool) =>
                                                <div className='digitalSHSATModule__dropdownContent'
                                                    onClick={() => handleDropdownTools(tool[0])}
                                                >
                                                    {dropdownToolsSelected.includes(tool[0]) ? tool[1] : tool[0]}
                                                    {dropdownToolsSelected.includes(tool[0]) && <FaCheck className='digitalSHSATModule__checkIcon'/>}
                                                </div>
                                            )}
                                        </div>
                                    }
                                    <div className='digitalSHSATModule__exit' 
                                        onClick={() => {
                                            setIsDropdownOpen(false);           
                                            setIsExitOpen(!isExitOpen)
                                        }}
                                    >
                                        Save and Exit
                                    </div>
                                    <div className='digitalSHSATModule__exit' onClick={handleDiscardAndExit}>
                                        Discard and Exit
                                    </div>
                                </div>
                            }
                        </div>                
                    </div>
                </div>
                <div className='digitalSHSATModule__headerBoundary' />
                <div className='digitalSHSATModule__headerBottom'>
                    {(!start && currentModule) &&<div className='digitalSHSATModule__headerBottomContents'>
                        <div>{testName.toUpperCase()}{modules.length===1&& `- ${sectionDisplayLabel(currentModule?.subject).toUpperCase()} ONLY`}{'  '}{'  '}/{'  '}{'  '}</div>
                            {(questionNumber === 0 || modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber))) && <div>{currentStandaloneBlock
                                ? `${sectionDisplayLabel(currentModule?.subject).toUpperCase()} – ${STANDALONE_ITEMS_LABEL.toUpperCase()}`
                                : currentPassageSet
                                ? `${sectionDisplayLabel(currentModule?.subject).toUpperCase()} – PASSAGE SET ${currentPassageSet.setIndex} OF ${currentPassageSet.setCount}`
                                : sectionDisplayLabel(currentModule?.subject).toUpperCase()}{'  '}{'  '}/{'  '}{'  '}</div>   }     
                            {(questionNumber !== 0 && modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber))) && <>
                            <div>{currentStandaloneBlock
                                ? `${currentStandaloneBlock.positionInBlock} OF ${currentStandaloneBlock.blockSize}`
                                : currentPassageSet
                                ? `${currentPassageSet.positionInSet} OF ${currentPassageSet.setSize}`
                                : `${sectionPosition} OF ${sectionTotal}`}</div>
                        </>}
                        {questionNumber !== 0 && !modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber)) && <div>END TEST</div>}
                    </div>}
                </div>
            </div>
            {/* body */}
            <div id='digitalSHSATModule__body' className='digitalSHSATModule__body'>
                {/* Line Reader*/}
                <div id='lineReaderDrag' className={dropdownToolsSelected.includes('Show Line Reader Mask')
                    ? 'digitalSHSATModule__popup--lineReader draggable-element'
                    : 'digitalSHSATModule__popup--hidden'
                }>
                    <div id='digitalSHSATModule__popuplineReader' className='digitalSHSATModule__popuplineReader'>
                        <div id='digitalSHSATModule__lineReaderResize' className='digitalSHSATModule__lineReaderResize'>
                            <SiZenn />
                        </div>
                        <div id='digitalSHSATModule__popuplineReaderContent' className='digitalSHSATModule__popuplineReaderContent'>
                            <div id='digitalSHSATModule__lineReaderControlBar' className='digitalSHSATModule__lineReaderControlBar draggable-element'>
                                {/* guide icon */}
                                <div className='digitalSHSATModule__lineReaderGuideIcon'>
                                    <IoMdMove />
                                </div>
                            </div>
                            {/* resize icon */}
                            <div className='digitalSHSATModule__lineReaderIconContainer'>
                                <div id='digitalSHSATModule__lineReaderTransparentResize' className='digitalSHSATModule__lineReaderTransparentResize'>
                                    <SiZenn />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* End-of-passage interstitial (page 11). Replaces the
                    question while armed; the live top bar (back / Review /
                    blue forward) stays above it. */}
                {showPassageInterstitial && <PassageInterstitial />}
                {/* Section directions (Revising/Editing Part A / Part B) shown
                    before the question; the live top bar's blue arrow
                    acknowledges it. */}
                {showSectionDirections && currentDirectionsPrompt && <SectionDirectionsScreen prompt={currentDirectionsPrompt} />}
                {showReadingDirections && <SectionDirectionsScreen prompt={READING_COMP_DIRECTIONS} />}
                {/* Per-passage-set START screen ("ELA – Passage Set X of N /
                    n Questions / Start"), shown before each ELA passage set —
                    the counterpart of the end-of-passage interstitial. Its
                    Start button (or the blue forward arrow) reveals the set's
                    first question. */}
                {showPassageSetStart && currentPassageSet && (
                    <PassageSetStart
                        sectionName={currentModule?.subject ?? null}
                        setIndex={currentPassageSet.setIndex}
                        setCount={currentPassageSet.setCount}
                        questionCount={currentPassageSet.setSize}
                        onStart={acknowledgePassageSetStart}
                    />
                )}
                {/* ELA "Stand alone items" (Part B) START screen — the
                    counterpart of the passage-set START, shown before the
                    stand-alone block. Its Start button (or the blue forward
                    arrow) reveals the first stand-alone question. */}
                {showStandaloneStart && currentStandaloneBlock && (
                    <PassageSetStart
                        sectionName={currentModule?.subject ?? null}
                        label={STANDALONE_ITEMS_LABEL}
                        questionCount={currentStandaloneBlock.blockSize}
                        onStart={acknowledgeStandaloneStart}
                    />
                )}
                {/* End-of-section screen for a NON-final section (locked flow):
                    "End of {section}" + Continue → next section. The FINAL
                    section keeps the terminal end screen below (re-titled). */}
                {lockNavigation && showSectionEnd && (
                    <SectionEndScreen
                        title={sectionEndTitle(currentModule?.subject, moduleEndsOnStandalone)}
                        allAnswered={moduleAllAnswered}
                        buttonLabel="Continue"
                        onButton={() => {
                            setShowSectionEnd(false);
                            // Cross the section boundary: navigateQuestion moves
                            // from the last question of this module to the next
                            // module's intro (questionNumber 0).
                            navigateQuestion(questionNumber + 1, currentModule.id);
                        }}
                    />
                )}
                {start
                ?<div className='digitalSHSATModule__startContainer'>
                    <div className='digitalSHSATModule__startInnerContainer'>
                        <div className='digitalSHSATModule__startInnerTop'>
                            <div className='digitalSHSATModule__startInnerTopHeader'>Test Start</div>
                            <div className='digitalSHSATModule__startInnerTopInfoBox'>
                                <div className='digitalSHSATModule__startInnerTopInfo'>{modules?.map(m => m.questions).flat().length} Questions</div>
                                <div className='digitalSHSATModule__startInnerTopInfo'>{isUntimed? 'Untimed': formatTime2(timeLeft)}</div>
                            </div>
                        </div>
                        <div className='digitalSHSATModule__startInnerBottom'>
                            {/* "Start with ELA or Math first" — only when the test
                                actually has both sections. The container applies
                                the choice in beginTest (orderByChoice). */}
                            {startSection && setStartSection && hasSectionChoice(modules ?? []) && (
                                <SectionStartPicker value={startSection} onChange={setStartSection} />
                            )}
                            <div className='digitalSHSATModule__startInnerBottomInfo'>When you click the Start button, your time will begin counting down.</div>
                            <div className='digitalSHSATModule__startInnerBottomButton' onClick={beginTest}>Start</div>
                        </div>
                    </div>
                </div>
                : (questionNumber === 0 || modules?.find(m=> m.questions.find(q=> q.questionNum === questionNumber))
                ? <div className='digitalSHSATModule__bodyContainer' style={(showPassageInterstitial || showSectionDirections || showPassageSetStart || showStandaloneStart || showSectionEnd || showReadingDirections) ? { display: 'none' } : undefined}>
                    {questionNumber !== 0
                    ? <>
                        {currentQuestion && currentQuestion?.passages &&
                            <div className='digitalSHSATModule__material'>
                                <div className='digitalSHSATModule__materialContent'>
                                    <div key={currentQuestion.passages.id} id='digitalSHSATModule__passage' className='digitalSHSATModule__passage'>
                                        <p
                                            id={currentQuestion.passages.id}
                                            className='katex digitalSHSATModule__passageText'
                                        >
                                            {currentQuestion.passages.passage}
                                        </p>
                                        <p
                                            id={`passage ${currentQuestion.passages.id}`}
                                            className='katex digitalSHSATModule__passageTextPassage'
                                            onMouseUp={handlePassageMouseUp}    
                                            onTouchEnd={handlePassageMouseUp}                                            
                                            dangerouslySetInnerHTML={{ __html: fullPassage?.html }}
                                        >
                                        </p>
                                        {/* annotation */}
                                        {currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.annotations.filter(annotation => (
                                            annotation.passageId === `${currentQuestion?.passages.id}`
                                            )).map((annotation, a) => <>
                                                <div
                                                    className={`katex
                                                            digitalSHSATModule__passageAnnotation
                                                            ${annotation.color && 'digitalSHSATModule__passageAnnotation--' + COLORS[annotation.color]}
                                                    `}
                                                    dangerouslySetInnerHTML={{ __html: annotation?.html }}
                                                ></div>
                                            </>)
                                        }
                                        {currentAnnotation && <AnnotationSHSATHelper
                                            id='digitalSHSATModule__passageAnnotationHelper'
                                            annotation={currentAnnotation}
                                            annotationColor={annotationColor}
                                            setAnnotationColor={setAnnotationColor}
                                            setCurrentAnnotation={() => setCurrentAnnotation(null)}
                                            setAnnotation={setAnnotation}
                                            deleteAnnotation={deleteAnnotations}
                                        />}
                                        {/* glossary definition */}
                                        {questionNumber > 0 && 
                                            definitions.map((definition, index)=>
                                                <div
                                                    id={`definition ${currentQuestion?.passages.id}`}
                                                    key={index}
                                                    className='katex digitalSHSATModule__Definition'
                                                    onMouseUp={handleDefinition}    
                                                    onTouchEnd={handleDefinition}  
                                                    dangerouslySetInnerHTML={{ __html: definition?.html }}
                                                ></div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                        <div className='digitalSHSATModule__question'>
                            <div className='digitalSHSATModule__questionContent'>
                                {currentQuestion?.question && !currentQuestion.question.includes('[[') && <span className='katex digitalSHSATModul__questionQuestion'>
                                    {currentQuestion?.question}
                                </span>}
                                {currentQuestion?.figures.map(figure => (
                                    <img
                                        className='digitalSHSATModule__figure'
                                        key={figure.id}
                                        src={figure.image}
                                        alt={(questionNumber).toString()}
                                    />
                                ))}
                            </div>
                            {currentQuestion?.questionType===1
                            ? <div className='digitalSHSATModule__questionContainer'>
                                {currentQuestion?.choices?.length > 0&&   
                                    <>
                                        {currentQuestion?.choices?.map((choice, i) => (
                                            <div key={choice.id}  
                                                className='digitalSHSATModule__questionChoiceWrapper'
                                            >
                                                <div 
                                                    className={toolsSelected !== 'Answer Eliminator' && currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id)
                                                        ? 'digitalSHSATModule__choiceContainer--disabled'
                                                        : 'digitalSHSATModule__choiceContainer'
                                                    }
                                                    onClick={() => {
                                                        if (toolsSelected === 'Answer Eliminator') crossOutChoice(choice.id);   
                                                        else currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id) || handleSelect(choice.id);
                                                    }}
                                                >
                                                    {toolsSelected === 'Answer Eliminator' && <div className="digitalSHSATModule__crossOutLine--hover"></div>}
                                                    {/* crossout */}
                                                    {currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id) && (
                                                        <div className="digitalSHSATModule__crossOutLine"></div>
                                                    )}
                                                    <div
                                                        className={currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id)
                                                            ? 'digitalSHSATModule__radioButton--disabled' 
                                                            : currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.answer === choice.id
                                                            ? 'digitalSHSATModule__radioButton--selected'
                                                            : 'digitalSHSATModule__radioButton'
                                                        }>
                                                            {currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.answer === choice.id?
                                                                <IoMdRadioButtonOn className='digitalSHSATModule__radioButtonIcon'/>
                                                                : <IoMdRadioButtonOff className='digitalSHSATModule__radioButtonIcon'/>
                                                            }
                                                    </div>
                                                    <div className='digitalSHSATModule__questionChoiceContent'>
                                                        <p className='digitalSHSATModule__choiceText'>
                                                            {MULTIPLE_CHOICES[i]}.
                                                        </p>
                                                        {choice.figure
                                                        ?   <img
                                                                className='digitalSHSATModule__questionChoiceFigure'
                                                                key={choice.figure}
                                                                src={choice.figure}
                                                                alt={choice.figure}
                                                            />
                                                        :   <p className='digitalSHSATModule__questionChoiceText'>
                                                                <span className='katex'>
                                                                    {choice.choice}
                                                                </span>
                                                            </p>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                }
                            </div>
                            : currentQuestion?.questionType===3
                            ? <div className='digitalSHSATModule__questionContainer'>
                                {currentQuestion?.choices?.length > 0 &&   
                                    <>
                                        {currentQuestion?.choices?.map((choice, i) => (
                                            <div key={choice.id}  
                                                className='digitalSHSATModule__questionChoiceWrapper'
                                            >
                                                <div 
                                                    className={toolsSelected !== 'Answer Eliminator' && currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut.includes(choice.id)
                                                        ? 'digitalSHSATModule__choiceContainer--disabled'
                                                        : 'digitalSHSATModule__choiceContainer'
                                                    }
                                                    onClick={() => {
                                                        if (toolsSelected === 'Answer Eliminator') crossOutChoice(choice.id);   
                                                        else currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id) || handleSelect(choice.id);
                                                    }}
                                                >
                                                    {toolsSelected === 'Answer Eliminator' && <div className="digitalSHSATModule__crossOutLine--hover"></div>}
                                                    {/* crossout */}
                                                    {currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id) && (
                                                        <div className="digitalSHSATModule__crossOutLine"></div>
                                                    )}
                                                    <div
                                                        className={currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.crossedOut?.includes(choice.id)
                                                            ? 'digitalSHSATModule__radioButton--disabled' 
                                                            : Array.isArray(currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.answer) && currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum).answer.includes(choice.id)
                                                            ? 'digitalSHSATModule__radioButton--selected'
                                                            : 'digitalSHSATModule__radioButton'
                                                        }>
                                                            {Array.isArray(currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.answer) && currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum).answer.includes(choice.id)?
                                                                <RiCheckboxFill className='digitalSHSATModule__radioButtonIcon'/>
                                                                : <RiCheckboxBlankLine className='digitalSHSATModule__radioButtonIcon'/>
                                                            }
                                                    </div>
                                                    <div className='digitalSHSATModule__questionChoiceContent'>
                                                        <p className='digitalSHSATModule__choiceText'>
                                                            {MULTIPLE_CHOICES[i]}.
                                                        </p>
                                                        {choice.figure
                                                        ?   <img
                                                                className='digitalSHSATModule__questionChoiceFigure'
                                                                key={choice.figure}
                                                                src={choice.figure}
                                                                alt={choice.figure}
                                                            />
                                                        :   <p className='digitalSHSATModule__questionChoiceText'>
                                                                <span className='katex'>
                                                                    {choice.choice}
                                                                </span>
                                                            </p>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                }
                            </div>
                            : currentQuestion?.questionType===4
                            ? <div className='digitalSHSATModule__questionContainer'>
                                {currentQuestion?.hotText?.length > 0 && 
                                    <div id='digitalSHSATModule__questionHelper' className='digitalSHSATModule__questionChoiceWrapper'>
                                        <p
                                            id={currentQuestion?.questionHelper?.id}
                                            className='katex digitalSHSATModule__questionHelperText'
                                            dangerouslySetInnerHTML={{ __html: questionHelper }}
                                            onClick={(e) => {
                                                const target = e.target as HTMLElement;
                                                if (target.classList.contains('digitalSHSATModule__questionHelperHotText')) {
                                                    const text = target.dataset.text || '';
                                                    const index = Number(target.dataset.index);
                                                    handleHotText(text, index); 
                                                }
                                            }}
                                        ></p>
                                    </div>
                                }
                            </div>
                            : currentQuestion?.questionType===5
                            ? <div className='digitalSHSATModule__questionContainer'>
                                <DragAndDropBucket
                                    question={currentQuestion}
                                    modules={modules}
                                    currentModule={currentModule}
                                    autoSave={autoSave}
                                    setModules={setModules}
                                />
                            </div>
                            : currentQuestion?.questionType===6
                            ? <div className='digitalSHSATModule__questionContainer'>
                                <DragAndDropBlank
                                    question={currentQuestion}
                                    modules={modules}
                                    currentModule={currentModule}
                                    autoSave={autoSave}
                                    setModules={setModules}
                                />
                            </div>
                            : currentQuestion?.questionType===7
                            ? <div className='digitalSHSATModule__questionContainer'>
                                <div className='digitalSHSATModule__questionChoiceWrapper'>
                                    <p
                                        className='katex digitalSHSATModule__questionHelperText'
                                        dangerouslySetInnerHTML={{ __html: fbQuestionHelper }}
                                        onBeforeInput={(e) => {
                                            // Math section: fill-in blanks accept only a numeric
                                            // answer (digits, one decimal, optional leading minus).
                                            // Reject other characters before they land and show a
                                            // brief popup. ELA blanks (word answers) are unaffected.
                                            if (currentModule?.subject !== 'Mathematics') return;
                                            const target = e.target as HTMLElement;
                                            if (!(target instanceof HTMLInputElement)
                                                || !target.classList.contains('digitalSHSATModule__questionHelperFB')) return;
                                            const data = (e.nativeEvent as InputEvent).data;
                                            if (!data) return;
                                            const start = target.selectionStart ?? target.value.length;
                                            const end = target.selectionEnd ?? target.value.length;
                                            const prospective = target.value.slice(0, start) + data + target.value.slice(end);
                                            const reason = validateMathEntry(prospective);
                                            if (reason) {
                                                e.preventDefault();
                                                setFbMathWarn(reason);
                                                if (fbWarnTimer.current) clearTimeout(fbWarnTimer.current);
                                                fbWarnTimer.current = setTimeout(() => setFbMathWarn(null), 2600);
                                            }
                                        }}
                                        onInput={(e) => {
                                            // Live signal so the locked-test forward arrow
                                            // enables as the student types — WITHOUT committing
                                            // to ``modules`` (a commit rebuilds the input on the
                                            // next render and would drop focus mid-typing).
                                            const target = e.target as HTMLElement;
                                            if (lockNavigation && target.classList.contains('digitalSHSATModule__questionHelperFB')) {
                                                const wrapper = target.closest('.digitalSHSATModule__questionChoiceWrapper');
                                                const anyFilled = wrapper
                                                    ? Array.from(wrapper.querySelectorAll('input.digitalSHSATModule__questionHelperFB'))
                                                        .some((el) => (el as HTMLInputElement).value.trim() !== '')
                                                    : (target as HTMLInputElement).value.trim() !== '';
                                                setLockLiveAnswered(anyFilled);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const target = e.target as HTMLElement;
                                            if (target.classList.contains('digitalSHSATModule__questionHelperFB')) {
                                                const input = target as HTMLInputElement;
                                                const index = Number(input.dataset.index);
                                                const value = input.value;
                                                handleFillintheBlank(value, index);
                                            }
                                        }}
                                    ></p>
                                    {fbMathWarn && (
                                        <div className='digitalSHSATModule__inputWarning' role='alert'>{fbMathWarn}</div>
                                    )}
                                </div>
                            </div>
                            : currentQuestion?.questionType===8
                            ? <div className='digitalSHSATModule__questionContainer'>
                                <TableGrid
                                    question={currentQuestion}
                                    modules={modules}
                                    currentModule={currentModule}
                                    autoSave={autoSave}
                                    setModules={setModules}
                                />
                            </div>
                            : currentQuestion?.questionType===9
                            ? <div className='digitalSHSATModule__questionContainer'>
                                <div className='digitalSHSATModule__questionChoiceWrapper'>
                                    <p
                                        className='katex digitalSHSATModule__questionHelperText'
                                        dangerouslySetInnerHTML={{ __html: icQuestionHelper }}
                                        onInput={(e) => {
                                            const target = e.target as HTMLSelectElement;
                                            if (target.classList.contains('digitalSHSATModule__questionHelperIC')) {
                                                const index = Number(target.dataset.index);
                                                const answer = target.value || '';
                                                handleInlineChoice(index, answer); 
                                            }
                                        }}
                                    ></p>
                                </div>
                            </div>
                            : currentQuestion?.questionType===10
                            ? <div className='digitalSHSATModule__questionContainerHotSpot'>
                                {currentQuestion?.choices?.length > 0 &&   
                                    <>
                                        {currentQuestion?.choices?.map((choice, i) => (
                                            <div 
                                                className={Array.isArray(currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.answer) && currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum).answer.includes(choice.id)
                                                    ? 'digitalSHSATModule__choiceContainerHotSpot--selected'
                                                    : 'digitalSHSATModule__choiceContainerHotSpot'
                                                }
                                                onClick={() => handleHotspotFigure(choice.id)}
                                            >
                                                {choice.figure
                                                ?   <img
                                                        className='digitalSHSATModule__questionChoiceFigure'
                                                        key={choice.figure}
                                                        src={choice.figure} 
                                                        alt={choice.figure}
                                                        draggable="false"
                                                    />
                                                :   <p className='digitalSHSATModule__questionChoiceText'>
                                                        <span className='katex'>
                                                            {choice.choice}
                                                        </span>
                                                    </p>
                                                }
                                            </div>
                                        ))}
                                    </>
                                }
                            </div>
                            : currentQuestion?.questionType===11 || currentQuestion?.questionType===12
                            ? <div className='digitalSHSATModule__questionContainer'>
                                <div className='digitalSHSATModule__eeContainer'>
                                    <math-field
                                        onInput={(e) => handleEquationEditor((e.target as any).value)}
                                        ref={mathfieldRef}
                                        style={{
                                            width: "620px",
                                            height: "50px",
                                            fontSize: "20px"
                                        }}
                                        className='digitalSHSATModule__eeInput'
                                        virtual-keyboard-mode="manual"
                                    >
                                        {currentModule?.answers?.find(answer => answer.questionNum===currentQuestion.questionNum)?.answer}
                                    </math-field>
                                </div>
                            </div>
                            : <></>
                            }
                        </div>                    
                    </>
                    : <div className='digitalSHSATModule__promptContainer'>
                        <div className='digitalSHSATModule__promptBox'>
                            <span className='katex'>{currentModule?.prompt}</span>
                        </div>
                    </div>
                    }
                </div>
                : (lockNavigation
                ? <SectionEndScreen
                    // FINAL section terminal (locked): per-section title +
                    // "All Questions Answered" + the existing final submit.
                    // (Non-final section boundaries use the Continue screen
                    // above; unlocked keeps its review-list terminal below.)
                    title={sectionEndTitle(currentModule?.subject, moduleEndsOnStandalone)}
                    allAnswered={moduleAllAnswered}
                    buttonLabel="Submit Final Answers"
                    onButton={handleSubmit}
                  />
                : <div className='digitalSHSATModule__endContainer'>
                    <div className='digitalSHSATModule__endHeader'>Congratulations, you have finished!</div>
                    <div className='digitalSHSATModule__endBodyContainer'>
                        <div className='digitalSHSATModule__endBodyContent'>
                            <h2 className='digitalSHSATModule__endBodyTitle'>End of Test {testNumber}</h2>
                            <div className='digitalSHSATModule__endBodyExpl'>{lockNavigation
                                ? <>When you are done, use the <span className='digitalSHSATModule__bold'>Submit Final Answers</span> button below to submit your answers.</>
                                : <>Use the <span className='digitalSHSATModule__bold'>Review</span> button above, or the list below, to go back and review your answers. When you are done, use the <span className='digitalSHSATModule__bold'>Submit Final Answers</span> button below to submit your answers.</>
                            }</div>
                            <button className='digitalSHSATModule__endBodyButton' onClick={handleSubmit}>Submit Final Answers</button>
                        </div>
                        {!lockNavigation && <div className='digitalSHSATModule__endBodyQuestions'>
                            <div className='digitalSHSATModule__endBodyQuestionExpl'>
                                <div className='digitalSHSATModule__endBodyIconCir' />
                                Unanswered questions are marked with a dot.
                            </div>
                            <div className='digitalSHSATModule__endBodyQuestion'>
                                {modules?.find((module) => module.subject === 'English Language Arts')?.questions?.map(question => 
                                    question.subject ==='English Language Arts' && 
                                    <div className='digitalSHSATModule__endBodyQuestionNumber'
                                        onClick={() => navigateQuestion(question.questionNum, modules?.find((module) => module.subject === 'English Language Arts')?.id)}
                                    >
                                        {(() => {
                                            const answerObj = modules?.find((module) => module.subject === 'English Language Arts')?.answers?.find(answer => answer.questionNum === question.questionNum);
                                            const isUnanswered = answerObj?.answer === null || answerObj?.answer === '' || answerObj?.answer.length === 0 || ((Array.isArray(answerObj.answer)&&Array.isArray(answerObj.answer[0])) && (answerObj.answer as [number, string][]).filter(( [, val]) => val !== "").length === 0);
                                            return isUnanswered ? (
                                                <div className='digitalSHSATModule__navigatorQuestionCir'/>
                                            ) : (
                                                <div className="digitalSHSATModule__navigatorQuestionNone" />
                                            );
                                        })()}
                                        Question {question.questionNum}
                                    </div>
                                )}
                                {modules?.find((module) => module.subject === 'Mathematics')?.questions?.map(question => 
                                    question.subject ==='Mathematics' && 
                                    <div className='digitalSHSATModule__endBodyQuestionNumber'
                                        onClick={() => navigateQuestion(question.questionNum, modules?.find((module) => module.subject === 'Mathematics')?.id)}
                                    >
                                        {(() => {
                                            const answerObj = modules?.find((module) => module.subject === 'Mathematics')?.answers?.find(answer => answer.questionNum === question.questionNum);
                                            const isUnanswered = answerObj?.answer === null || answerObj?.answer === '' || answerObj?.answer.length === 0 || ((Array.isArray(answerObj.answer)&&Array.isArray(answerObj.answer[0])) && (answerObj.answer as [number, string][]).filter(( [, val]) => val !== "").length === 0);
                                            return isUnanswered ? (
                                                <div className='digitalSHSATModule__navigatorQuestionCir'/>
                                            ) : (
                                                <div className="digitalSHSATModule__navigatorQuestionNone" />
                                            );
                                        })()}
                                        Question {question.questionNum}
                                    </div>                                                    
                                )}
                            </div>
                        </div>}
                    </div>
                </div>))
                }
            </div>

            {/* Magnifier lens — rendered OUTSIDE the body so cloning the body
                content doesn't recurse into the lens. Bare draggable lens;
                toggled off from the tools dropdown ("Disable Magnifier"). */}
            <MagnifierLens
                active={dropdownToolsSelected.includes('Enable Magnifier')}
            />
        </div>
    )
}

export default DigitalSHSATModule;